import { Component, createRef } from 'react';
import styles from './editor.module.scss';
import { StageComponent, ViewMode, Preview, StageThumb } from './stage/stage.component';
import { InspectorComponent, ToolTabs, SaveFormat, OutputInfo, LOSSY, BulkInspector } from './inspector/inspector.component';
import { EditState, CropOp, Tool, Size, IDENTITY, initialState, outputSize, orientedSize, resetAll, resetTool, outputFilename, outputName, isApplied, currentIndex, cropView, savedSize, fineTurn } from './model/edit-stack';
import { renderOutput, renderPreview, formatBytes, scaleTo, MipCache, Source } from './model/render';
import { fitThumb, thumbKey, isEdited, thumbOut, renderThumb } from './model/thumbs';
import { estimateOutputBytes, primeEstimateSource, sizeDelta } from './model/estimate';
import { CropPolicy, BATCH_LIMIT, OwnCrop, takeOwnCrop, resetPhoto, etaLabel, runPool, stateForMember, exportStateForMember, foldEdit, BatchMember, batchCounts, failureText, failureOffersReplace, batchOutputSummary, isUnsized, hasOwnCrop } from './model/batch';
import { canRetry } from './server/errors';
import { probeServerFile } from './batch/probe-file';
import { saveSequentially, DOWNLOAD_GAP_MS, eachDoneText } from './batch/save-each';
import { withinBrowserBudget } from './codecs/routing';
import { detectFormat, canDecodeInBrowser, sniffFormat } from './codecs/detect';
import { friendlyFailure } from './bulk-gate';
import { readHead } from './codecs/canvas-utils';
import type { ServerJob, ServerProgress, ServerErrorCode } from './server';
import { zipEntries, zipNameFor, splitForZip, zipPartName, uniqueNames } from './batch/zip';
import type { ImageFormat, DecodedImage } from './codecs/types';
import type { LandingSpec } from './routing';
import { formatLabel } from './routing';
import { MotiveComponent } from '../ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import { MotiveLocationTypes } from '../../utils/request.utils';

export interface EditorItem {
    id: string;
    file: File;
    decoded: DecodedImage | null;
    error: string | null;
    state: EditState | null;
    thumb: HTMLCanvasElement | null;
    /** The strip card's picture (owner 2026-10-10: the cards follow the live edit): this file's effective edit drawn at
     *  ≤ THUMB_EDGE px, keyed by model/thumbs thumbKey so an unchanged file is never redrawn; the untouched case reuses
     *  `thumb` itself. `thumb` stays the untouched source at ≤128 px: the card's picture until this is drawn, and the
     *  render source once the photo's pixels were let go (`released`) - never a decode just for a card. */
    strip?: { key: string; canvas: HTMLCanvasElement } | null;
    /** Downscaled copies of the source, built once per scale and shared by previews and the export. */
    mips: MipCache;
    // ---- batch states (PLAN C5). With `bulk` off: route 'browser', ownCrop null, and nothing reads the rest.
    route: 'browser' | 'server';
    status: 'queued' | 'preparing' | 'done' | 'failed';
    progress: ServerProgress | null;
    /** retryAfterSec (critic-5): the server's wait on a 429, kept so the box can say how long. */
    failure: { code: ServerErrorCode | 'decode' | 'encode'; message: string; retryAfterSec?: number } | null;
    /** Per-file crop override ("Only the selected photo"): a box in THIS photo's source pixels (model/own-crop.ts).
     *  The only place an own crop lives; its pipeline = the batch's ops without crops + this crop last. */
    ownCrop: OwnCrop | null;
    result: Blob | null;
    resultName: string | null;
    /** What `result` was made from (file + edit + output settings, see resultKey()); a later export reuses
     *  `result` only while this still matches — no second upload / counted download for a file already done. */
    resultKey: string | null;
    job: ServerJob | null;
    /** Bulk: the real size of a server-route file the browser does not open, from its header (TIFF IFD,
     *  EPS bounding box, HEIF ispe, ICO…; SP-1, QA round 2 N1); null = unknown (RAW). */
    probed?: Size | null;
    /** Bulk: the pixel size of `result`, read from the bytes our servers returned. */
    resultSize?: Size | null;
    /** Bulk: the upload handle of this file's last server run — a re-export schedules it again instead
     *  of sending the bytes once more (SP-7). */
    uploaded?: unknown;
    /** Bulk: this file's bytes reached our servers this session (also when it then failed) - the privacy
     *  line never says "nothing is uploaded" after that (formats-6, power-7, critic-2). */
    sent?: boolean;
    /** Bulk: when this file's current run started (preparing ETA, PLAN C3). */
    startedAt?: number;
    /** Routing ruling 2026-10-07: the browser tried and could not decode this file, so it moved to our servers.
     *  Its bytes are unverified: never handed back untouched as "Done" (J9-2, J16-6). */
    decodeFailed?: boolean;
    /** Bounded memory (J3-1/J3-3): the decoded pixels were let go (size, thumb and meta kept); ensureDecoded
     *  decodes the file again when it is shown or exported. */
    released?: boolean;
    /** C1: how many times "Try again" re-ran this file (capped by MAX_RETRIES). */
    retries?: number;
}

type ServerModule = typeof import('./server');
/** One file's output from an export run; `size`/`upload` only from our servers. */
interface RunResult { name: string; blob: Blob; size?: Size | null; upload?: unknown }
/** Bulk: files above this size on the server route are not decoded in the browser just for a thumbnail. */
const SERVER_PREVIEW_MAX_BYTES = 30 * 1024 * 1024;
/** Server edits in flight per user (no global caps — owner 2026-09-11). */
const SERVER_CONCURRENCY = 2;
/** Bounded memory (usage QA J3-1/J3-3, PLAN A ruling 2): decoded pixels kept for photos OTHER than the
 *  selected one (64 MP = 256 MB RGBA). Beyond it the largest are let go and decoded again on demand, so a batch
 *  of 34 x 24 MP TIFFs never holds 34 decoded images. */
const RETAIN_PX = 64e6;
/** Files above this size have their header read for the 300 MP browser budget before any decode. */
const BUDGET_PROBE_BYTES = 16 * 1024 * 1024;
/** A memory failure inside the browser (Chrome: "Array buffer allocation failed"), said in plain words. */
const OOM_RE = /allocation failed|out of memory|invalid array length|RangeError/i;
const OOM_TEXT = 'your browser ran out of memory for this file';
/** Ruling 6 (PLAN): when the format does not change, the output keeps the source's extension spelling
 *  (`.tif` stays `.tif`, a `.jfif` saved as JPG stays `.jfif`) - in the file, "Saved as" and "Done". */
export function nameFor(sourceName: string, size: Size, extension: string, sourceFormat: ImageFormat | undefined): string {
    return outputName(sourceName, size, extension, sourceFormat);   // F1 (model/edit-stack)
}
/** The quality slider's starting value: moving it is an edit (firsttime-3). */
const DEFAULT_QUALITY = 85;
/** Toast placement for the notices above (L5 styles .undoNote; this keeps them on screen until it does). */
const TOAST: React.CSSProperties = { position: 'fixed', left: '50%', top: 16, transform: 'translateX(-50%)', zIndex: 1050, maxWidth: 'min(560px, calc(100vw - 32px))', background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,.18)', padding: '10px 14px', borderRadius: 8, fontSize: 14 };
/** The per-file first-frame line (edited, or IMAGEEDITOR-09 the line shown at open), dropped from a batch's
 *  selected-file note (the batch counts them instead). */
const INCOMPLETE_RE = /This file looks incomplete \(it ends early\)\. The missing part will be blank\./;
const FIRST_FRAME_RE = /Only the first frame is kept \(animated (?:GIF|APNG|WebP)\)\.|Animated (?:GIF|APNG|WebP): an edit here keeps only the first frame\./;
/** IMAGEEDITOR-02b: how an animated source is named in the notes. */
function animLabel(f: ImageFormat | undefined): string { return f === 'apng' || f === 'png' ? 'APNG' : f === 'webp' ? 'WebP' : 'GIF'; }
/** L6 2026-10-11 (APNG note): true when writing `src` as `out` is "its own format", so with no edit the file's own bytes
 *  are the download. An APNG has no browser encoder (codecs/detect ENCODE_FALLBACK apng -> png), so "Same as original"
 *  writes PNG; an unedited APNG therefore downloads as itself, animation kept, exactly like an unedited GIF or WebP.
 *  Before this, the PNG write made every APNG a still PNG even with no edit, against the open note below. */
export function keepsOwnFormat(src: ImageFormat, out: ImageFormat): boolean { return out === src || (src === 'apng' && out === 'png'); }
/** IMAGEEDITOR-09: an animated file opened and not edited yet - downloading it unchanged keeps the animation
 *  (its own bytes), so the line says what an edit would do, at once (not only after the first edit). */
export function animationNote(d: Pick<DecodedImage, 'sourceFormat' | 'animated'>): string | null {
    return d.animated ? `Animated ${animLabel(d.sourceFormat)}: an edit here keeps only the first frame.` : null;
}
/** IMAGEEDITOR-09: where "Keep the animation" goes (the GIF Maker picks the file up from ?xfer, shared item S1). */
export const GIF_MAKER_PATH = '/gif-maker';
export const GIF_MAKER_ACTION = 'Keep the animation: open GIF Maker →';
/** R2-save-as-format-quality-matrix-7: formats that write no transparency at all (the encoder mattes onto white).
 *  BMP too: review9 R2 matrix-2 made codecs/encode.ts flatten it onto white (24-bit), like JPG / PPM (IMG-QA1-fixcheck-1). */
const OPAQUE_OUT: ImageFormat[] = ['jpg', 'ppm', 'bmp'];
/** R2-save-as-format-quality-matrix-7: what the chosen format does to a source with transparency; null = nothing lost. */
export function alphaNote(hasAlpha: boolean, fmt: ImageFormat, corners = false): string | null {
    if (!hasAlpha) return null;
    // R2 matrix-2: an opaque photo whose edit leaves transparent corners (circle, a fine turn) says "corners"
    if (OPAQUE_OUT.includes(fmt)) return `${formatLabel(fmt)} has no transparency: the ${corners ? 'corners' : 'background'} will be white.`;
    // codecs/gif.ts cuts alpha at one bit (oneBitAlpha): a soft pixel turns fully clear or fully opaque, not matted
    if (fmt === 'gif') return 'GIF has 1-bit transparency: soft edges and shadows become hard-edged.';
    return null;
}
/** IMAGEEDITOR-21: Download clicks within this long after a save are ignored (a double-click saves one copy). */
export const SAVE_GUARD_MS = 1000;
/** IMAGEEDITOR-41c: the busy line while the selected file is being opened (HEIC takes 3-7 s to convert). */
export function openingLabel(f: File | undefined): string {
    return f && detectFormat(f) === 'heic' ? 'Converting iPhone photo (HEIC)…' : 'Opening…';
}
/** IMAGEEDITOR-11: the preparing line - the time left when known, else which file is being made; "Starting…"
 *  only before the first file is done. */
export function prepText(done: number, total: number, eta: string): string {
    if (eta) return `${eta.replace(/^~/, 'about ')} left`;
    if (done >= total) return 'Finishing…';
    return done === 0 ? 'Starting…' : `File ${done + 1} of ${total}`;
}
/** IMAGEEDITOR-18b: how long "Removed <name> · Undo" stays. */
export const REMOVED_UNDO_MS = 6000;
/** A button the output block offers beside its notes (IMAGEEDITOR-09 GIF Maker, R2-7 Keep PNG). */
export interface OutputAction { label: string; onClick: () => void; testId: string }
/** OutputInfo plus the editor's actions for the notes (rendered by the inspector above Download). */
export interface EditorOutputInfo extends OutputInfo { actions?: OutputAction[] }
/** power-13 / formats-5 (browser half): a multi-page TIFF or an animated GIF/APNG edited here keeps only its
 *  first page/frame — said per file, with the count, next to the output line. Null when nothing is lost. */
export function keptNote(d: Pick<DecodedImage, 'sourceFormat' | 'animated' | 'pageCount' | 'note'>, out?: { format: ImageFormat; size: Size }): string | null {
    if (d.animated) return `Only the first frame is kept (animated ${animLabel(d.sourceFormat)}).`;
    // the TIFF's other caveats (CMYK, colour profile, 16-bit) stay beside the page line
    if (d.pageCount && d.pageCount > 1) return [`Only the first page is kept (${d.pageCount} pages).`, otherNotes(d.note)].filter(Boolean).join(' ');
    // other codec caveats (16-bit PNG, multi-size ICO) come through as the decoder worded them, fitted to the
    // output actually written: J4-5 / J9-7 - no "saved as an 8-bit PNG" while JPG or ICO is written;
    // J9-6 - a multi-size ICO saved at 32 px does not say "the 256 px icon is kept"
    let n = d.note ?? null;
    if (n && out && out.format !== 'png') n = n.replace(/,?\s*so it is saved as an 8-bit PNG\.?/i, '.');
    if (n && out && out.format === 'ico') {
        const edge = Math.max(out.size.width, out.size.height);
        n = n.replace(/Only the (\d+) px icon is kept/i, (m, px) => (Number(px) === edge ? m : `The ${px} px icon is edited and saved as one ${edge} px icon`));
    }
    return n;
}
/** The decoder's caveats without its TIFF page sentence (said by keptNote / openNote in the editor's words). */
const TIFF_PAGES_RE = /This TIFF has \d+ pages\. Only the first page is edited and saved; the other pages are not kept\.\s*/;
function otherNotes(n: string | undefined): string | null { return n ? n.replace(TIFF_PAGES_RE, '').trim() || null : null; }
/** IMAGEEDITOR-20 / R2-colour-profile-fidelity-3 / -4n / IMG-QA1-usecases-1: what the decoder found (a file that
 *  ends early, CMYK, a colour profile converted to sRGB, the pages of a TIFF) is said as soon as the file opens,
 *  not only after the first edit. An untouched download is the file's own bytes (every page kept), so the page
 *  line says what an edit does, like the animated line. */
export function openNote(d: Pick<DecodedImage, 'sourceFormat' | 'animated' | 'pageCount' | 'note'>): string | null {
    if (d.animated) return animationNote(d);
    const pages = d.pageCount && d.pageCount > 1 ? `This TIFF has ${d.pageCount} pages: an edit here keeps only the first page.` : null;
    const other = otherNotes(d.note);
    return [other && untouchedWords(other), pages].filter(Boolean).join(' ') || null;
}
/** review10 loop-review: at open the download is still the file's own bytes, so the decoder's conversions are said as
 *  what an edit does ("an edit here is saved in sRGB"), never as done ("converted to sRGB"). After an edit keptNote
 *  says them as written. */
export function untouchedWords(n: string): string {
    return n
        .replace(/\b(Display P3 photo|Adobe RGB scan|ProPhoto RGB image), converted to sRGB(?: for the web)?\./g, '$1: an edit here is saved in sRGB.')
        .replace(/This image's "([^"]*)" colour profile was converted to sRGB\./g, 'This image has the "$1" colour profile: an edit here is saved in sRGB.')
        .replace(/This image's colour profile was converted to sRGB\./g, 'This image has a colour profile: an edit here is saved in sRGB.')
        .replace(/CMYK converted to RGB; dark tones may look lighter\./g, 'CMYK image: an edit here is saved in RGB, and dark tones may look lighter.')
        .replace(/so the saved image has 8 bits per channel\./g, 'so an edit here is saved with 8 bits per channel.');
}
const NOTHING_TO_SAVE = 'Nothing to download: none of these files could be read. Replace or remove them, or add other photos.';
/** regression-3 (final sweep): the "nothing to download" message names the files that could not be read. */
function nothingToSave(names: string[]): string {
    if (!names.length) return NOTHING_TO_SAVE;
    const shown = names.length > 5 ? [...names.slice(0, 4), `${names.length - 4} more`] : names;
    const list = shown.length === 1 ? shown[0] : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
    return `Nothing to download: none of these files could be read (${list}). Replace or remove ${names.length === 1 ? 'it' : 'them'}, or add other photos.`;
}
/** regression-3 (final sweep): the stage line when no file of the batch could be read. */
const NONE_READABLE = 'None of these files could be read, so there is nothing to edit or download. Replace or remove them, or add other photos.';
/** IMAGEEDITOR-29-30b: the same, counted ("Neither file" for two). */
export function noneReadableText(n: number): string {
    if (n === 2) return 'Neither file could be read, so there is nothing to edit or download. Replace or remove them, or add other photos.';
    return n > 2 ? `None of these ${n} files could be read, so there is nothing to edit or download. Replace or remove them, or add other photos.` : NONE_READABLE;
}
/** IMAGEEDITOR-28: the stage line when the open file could not be read (the error itself is said once, above). */
const NOTHING_TO_PREVIEW = 'Nothing to preview.';
const OFFLINE = "You're offline. Connect to the internet and try again.";
/** IMG-QA1-robust-1: the decoders' code could not be downloaded. */
export const CODECS_FAILED = "Couldn't load the editor: the connection was lost. It tries again when you're back online.";
const CODECS_WAIT = 'Waiting for the connection…';

/** A decoded source still holding pixels (trimSources zeroes a canvas / closes a bitmap to free it). */
function alive(src: unknown): boolean { const c = src as { width?: number; height?: number } | null; return !c || c.width === undefined || (c.width > 0 && (c.height ?? 1) > 0); }
function newItem(f: File, i: number): EditorItem {
    return { id: `${Date.now()}-${i}-${f.name}`, file: f, decoded: null, error: null, state: null, thumb: null, mips: new Map(),
        route: 'browser', status: 'queued', progress: null, failure: null, ownCrop: null, result: null, resultName: null, resultKey: null, job: null };
}

export interface EditorProps {
    files: File[];
    landing: LandingSpec;
    onChangeFiles: () => void;      // "Change" → back to the empty state
    onAddMore: () => void;          // opens the picker again (root keeps the list)
    onTelemetry?: (event: string, attrs?: Record<string, unknown>) => void;
    /** Flag imageEditorBulk (PLAN C2): server route, per-file crop, preparing / failure states. Default false. */
    bulk?: boolean;
    /** The editor removed / replaced files (× on a thumb, Replace): the root's list follows, so a later
     *  "Add more" appends to what is really open. Optional: absent = the root keeps its list. */
    onFilesChange?: (files: File[]) => void;
    /** Why a chosen file is not in the batch (51st file, not an image, already in it): shown over the
     *  editor, not only on the empty state (power-3, critic-6). */
    notice?: string | null;
    onDismissNotice?: () => void;
}

/** The model the views derive from: the batch, the photo it was drawn on, and each photo's own crop. */
interface ModelSnap { batch: EditState | null; owner: string | null; own: Record<string, OwnCrop | null>; }

interface EditorState {
    /** J14-1 (verify round): formats this browser failed to write at run time (AVIF -> WebP): every later
     *  name, "Saved as", "Done" and the format note use what was really written. */
    runtimeFallback?: Partial<Record<ImageFormat, ImageFormat>>;
    items: EditorItem[];
    selected: number;
    tool: Tool;
    view: ViewMode;
    zoom: number;
    format: SaveFormat;
    quality: number;
    cropPolicy: CropPolicy;
    /** Undo / redo (QA round 4 OC4-3): the batch MODEL before each user action (a whole drag is one), on any
     *  photo — every photo's view is a function of it, so stepping back restores every photo at once. */
    past: ModelSnap[];
    future: ModelSnap[];
    /** IMG-QA1-robust-1: the decoders' chunk failed to load (offline); Try again / back online retries. */
    codecsFailed?: boolean;
    preview: Preview | null;
    sourcePreview: Preview | null;
    basePreview: Preview | null;   // Crop mode: the image the ops before the current crop produced (additive model)
    busy: string | null;
    exporting: { done: number; total: number; label: string } | null;
    fatal: string | null;
    fullscreen: boolean;
    fsSupported: boolean;      // set on mount; iOS Safari has no element fullscreen → no ⛶ (UX review U1)
    boxHeight: number | null;  // phone canvas box height, chosen once per photo (UX review M4)
    canShare: boolean;         // navigator.share with files (phones): "Share…" beside Download (UX review U7)
    angleDragging: boolean;    // fine-angle slider held → thirds grid over the photo (UX review N3)
    /** Bulk: a batch run in progress ("7 of 12 · ~20 s"). */
    preparing: { done: number; total: number; startedAt: number } | null;
    /** "Reset all" / Reset just wiped edits: an inline notice with Undo (phone-5). */
    resetNote: string | null;
    /** The failed files the user already got a ZIP without: the failure box steps aside (formats-11). */
    failureAck: string | null;
    /** a11y-10 (final sweep): how a batch with a failed file ended, for screen readers (the inspector
     *  announces a clean finish itself). Cleared when the next export starts. */
    outcome: string;
    /** IMAGEEDITOR-09: featureFlags.gifMaker is on, so "Keep the animation" can open the GIF Maker. */
    gifMaker?: boolean;
    /** IMAGEEDITOR-18b: the file just removed with its ×, kept for "Undo" (REMOVED_UNDO_MS) at its place. */
    removed?: { item: EditorItem; index: number } | null;
}

const DESKTOP = () => typeof window !== 'undefined' && window.innerWidth >= 901;
const SNAP_TOL = 20; // px from top/bottom within which the editor snaps to fill the screen
const TOOL_LIST: Tool[] = ['resize', 'crop', 'rotate', 'flip'];

// Cap on a preview canvas's longer edge: previews render at the screen's device resolution for the
// current zoom, so this only bounds memory (a 3× phone box ≈ 1,200 px; a 2× desktop box ≈ 2,700 px).
const PREVIEW_EDGE = () => (typeof window !== 'undefined' && window.innerWidth <= 900 ? 1600 : 3200);

// Strip cards (model/thumbs): edits arrive at slider rate, so a pass waits for a THUMB_DEBOUNCE_MS pause, yet never
// longer than THUMB_MAX_WAIT_MS into a long drag; drawn cards reach the strip in groups at most THUMB_FLUSH_MS apart
// (the selected photo's at once), not one React commit per card for 50 files.
const THUMB_DEBOUNCE_MS = 150;
const THUMB_MAX_WAIT_MS = 600;
const THUMB_FLUSH_MS = 80;
/** A turn given back to the page (the stage preview, input) between two cards: an idle period when the browser has them. */
const idle = (): Promise<void> => new Promise(r => {
    const w = typeof window !== 'undefined' ? (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }) : null;
    if (w?.requestIdleCallback) w.requestIdleCallback(() => r(), { timeout: 200 }); else setTimeout(r, 0);
});
/** A change to the items that can change a card: a file added / removed / replaced, its view state, own crop or
 *  source thumb - not the cards themselves being written (renderThumbs' own commits), which would start a pass that
 *  cancels the one writing them. */
function thumbInputsChanged(a: EditorItem[], b: EditorItem[]): boolean {
    if (a.length !== b.length) return true;
    return b.some((y, i) => { const x = a[i]; return x.id !== y.id || x.state !== y.state || x.ownCrop !== y.ownCrop || x.thumb !== y.thumb; });
}

/** critic-5: the server's wait in seconds - the error's `retryAfterSec`, else "Try again in N minutes" in
 *  its message (an upload 429 carries the server's sentence only in the wrapped cause's message). */
function waitOf(e: any): number | undefined {
    if (typeof e?.retryAfterSec === 'number' && e.retryAfterSec > 0) return e.retryAfterSec;
    for (const t of [e?.userMessage, e?.message]) {
        const m = typeof t === 'string' ? /try again in (\d+)\s*(second|minute|hour)/i.exec(t) : null;
        if (m) return Number(m[1]) * (/^h/i.test(m[2]) ? 3600 : /^m/i.test(m[2]) ? 60 : 1);
    }
    return undefined;
}

/**
 * The workspace: owns the batch, the per-item edit states, previews and export. The selected item's
 * state is the "master"; in a batch the other items get a translation of it at export time
 * (model/batch.ts). Decoding is lazy per item; the first file decodes immediately.
 */
export class EditorComponent extends Component<EditorProps, EditorState> {
    private codecs: typeof import('./codecs') | null = null;
    private renderToken = 0;
    private previewTimer: ReturnType<typeof setTimeout> | null = null;
    private thumbTimer: ReturnType<typeof setTimeout> | null = null;
    private thumbGen = 0;
    private thumbAsked = 0;   // when the pending pass was first asked for (THUMB_MAX_WAIT_MS)
    private live = true;
    private editorRef = createRef<HTMLDivElement>();

    constructor(props: EditorProps) {
        super(props);
        this.state = {
            items: props.files.slice(0, BATCH_LIMIT).map(newItem),
            // (format below: reconcileFormat() drops a page default no file will be written in - regression-6)
            selected: 0, tool: props.landing.tool, view: 'fit', zoom: 1,
            // J12-2: the edge cannot write EPS / RAW back - an EPS page saves PNG by default, single and batch alike
            format: props.landing.defaultFormat === 'eps' ? 'png' : props.landing.defaultFormat === 'raw' ? 'jpg' : props.landing.defaultFormat,
            quality: 85, cropPolicy: 'centre-aspect', past: [], future: [], preview: null, sourcePreview: null, basePreview: null, busy: openingLabel(props.files[0]), exporting: null, fatal: null, fullscreen: false,
            fsSupported: false, boxHeight: null, canShare: false, angleDragging: false, preparing: null, resetNote: null, failureAck: null, outcome: '',
        };
    }

    async componentDidMount() {
        if (typeof window !== 'undefined') {
            document.addEventListener('fullscreenchange', this.onFsChange);
            // Listen in the capture phase on both window and document so we catch the settle no matter
            // which element is the page's scroll container (this layout scrolls <body>, not the window).
            window.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
            document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
            window.addEventListener('keydown', this.onKey);
            window.addEventListener('resize', this.onResize);
            // set after hydration so the SSR markup (no ⛶) matches the first client render
            if (document.fullscreenEnabled) this.setState({ fsSupported: true });
            // Tell the global sticky anchor to step aside while the loaded editor is open: the editor
            // carries its own reserved bottom ad band (.editorAdBand), so there must not also be a
            // floating anchor over it. The anchor watches this attribute (MutationObserver).
            document.documentElement.setAttribute('data-xc-editor', '1');
            this.watchAds();
            try { const n: any = navigator; if (n.canShare && n.canShare({ files: [new File([''], 'x.png', { type: 'image/png' })] })) this.setState({ canShare: true }); } catch { /* no Web Share */ }
        }
        // load the saver now: a Download pressed offline later must not die on a chunk load (critic-3)
        EditorComponent.loaders.fileSaver().catch(() => undefined);
        if (!await this.loadCodecs()) return;
        await this.startDecoding();
    }
    /** IMG-QA1-robust-1: the decoders' code is a lazy chunk. If it cannot load (the connection dropped after the
     *  page loaded) the editor says so with Try again, and tries again by itself when the browser is back online -
     *  never an endless "Opening…" with a 1 x 1 size and a Download that does nothing. */
    private async loadCodecs(): Promise<boolean> {
        try {
            this.codecs = await EditorComponent.loaders.codecs();
        } catch {
            if (!this.live) return false;
            this.setState({ codecsFailed: true, busy: CODECS_WAIT });
            if (typeof window !== 'undefined') window.addEventListener('online', this.retryCodecs);
            return false;
        }
        if (typeof window !== 'undefined') window.removeEventListener('online', this.retryCodecs);
        if (this.state.codecsFailed && this.live) this.setState({ codecsFailed: false, busy: openingLabel(this.current?.file) });
        return true;
    }
    private retryingCodecs = false;
    private retryCodecs = async () => {
        if (this.retryingCodecs || this.codecs || !this.live) return;
        this.retryingCodecs = true;
        try { if (await this.loadCodecs()) await this.startDecoding(); } finally { this.retryingCodecs = false; }
    };
    /** The rest of the mount, once the decoders are in: open the selected photo, then the others. */
    private async startDecoding() {
        if (this.props.bulk) await this.loadServerRoutes();
        this.reconcileFormat();
        await this.ensureDecoded(this.state.selected);
        this.reconcileFormat();
        this.schedulePreview(true);
        // Once the image is actually in, centre the editor so it opens as its own full-screen workspace
        // (owner request 2026-09-06). After decode + a frame the layout is settled, so this holds — an
        // earlier attempt fired at mount and got undone.
        if (typeof window !== 'undefined') requestAnimationFrame(() => { if (this.live && DESKTOP() && !document.fullscreenElement) this.snapEditor('start'); });
        // J3-5 (verify round): read every other file's size from its header now, so the batch's output line has a
        // summed estimate at once instead of none until each 24 MP TIFF has been decoded in turn
        // (not awaited: header reads run beside the background decode, never ahead of it)
        void Promise.all(this.state.items.filter(x => !x.decoded && !x.error && x.probed === undefined && detectFormat(x.file) !== 'unknown').map(x => this.probeItem(x.id).catch(() => null)));
        // decode the rest in the background for thumbnails
        for (let i = 0; i < this.state.items.length; i++) { if (!this.live) return; if (i !== this.state.selected) await this.ensureDecoded(i); }
        this.reconcileFormat();
    }

    /** Module loaders, overridable in specs (jest cannot mock a dynamic import() under vm-modules). */
    static loaders = {
        codecs: () => import('./codecs'),
        fileSaver: () => import('file-saver') as Promise<any>,
        server: () => import('./server'),
        transferTray: () => import('../../services/transfer-tray.service'),
        navigate: (url: string) => { if (typeof window !== 'undefined') window.location.assign(url); },
        /** IMAGEEDITOR-09: featureFlags.gifMaker (fail-closed: anything but true is off). */
        gifMakerOn: async (): Promise<boolean> => {
            const [{ container }, { SettingsService }, { isGifMakerOn }] = await Promise.all([import('../../context/module'), import('../../services/settings.service'), import('../gif-maker/gate')]);
            return new Promise<boolean>(resolve => {
                let sub: { unsubscribe(): void } | null = null; let done = false;
                sub = container.get(SettingsService).featureFlags.subscribe((f: unknown) => { if (!done) { done = true; resolve(isGifMakerOn(f as { gifMaker?: unknown })); sub?.unsubscribe(); } });
                if (done) sub.unsubscribe();
            });
        },
    };
    /** IMAGEEDITOR-09: read the GIF Maker flag once, the first time an animated file is open. */
    private gifFlagAsked = false;
    private askGifMaker() {
        if (this.gifFlagAsked) return; this.gifFlagAsked = true;
        EditorComponent.loaders.gifMakerOn().then(on => { if (on && this.live) this.setState({ gifMaker: true }); }).catch(() => undefined);
    }
    /** IMAGEEDITOR-09: "Keep the animation" - the file is stashed in the transfer tray and the GIF Maker opens with
     *  ?xfer=<id> (it picks the file up from there); without a tray (private mode, full) it opens empty. */
    private openGifMaker = async () => {
        const it = this.current; if (!it) return;
        let id: string | null = null;
        try { const { transferTray } = await EditorComponent.loaders.transferTray(); id = await transferTray.put([it.file], this.props.landing.slug, 'Image editor'); } catch { id = null; }
        this.props.onTelemetry?.('EDITOR_GIF_MAKER', { format: it.decoded?.sourceFormat, handedOff: !!id });
        EditorComponent.loaders.navigate(id ? `${GIF_MAKER_PATH}?xfer=${encodeURIComponent(id)}` : GIF_MAKER_PATH);
    };
    private server: ServerModule | null = null;
    /** Bulk only: load the server client and route each file (PLAN C3 routeFor). Opens on the first
     *  file the browser can show when the first one is server-only (EPS, RAW). */
    private async loadServerRoutes() {
        try { this.server = await EditorComponent.loaders.server(); } catch { return; /* no server client: everything stays in the browser */ }
        const srv = this.server;
        const items = this.state.items.map(it => ({ ...it, route: srv.routeFor(detectFormat(it.file), true) }));
        const first = items.findIndex(it => this.browserCanShow(it));
        await new Promise<void>(r => this.setState({ items, selected: first > 0 ? first : 0 }, r));
        await Promise.all(items.filter(it => !this.browserCanShow(it)).map(it => this.probeItem(it.id)));
    }
    /** A file routed to our servers without a browser decode: read its real size from the header (TIFF,
     *  EPS, HEIF, ICO…) so it shows, crops and resizes in real pixels — one file exactly as a batch
     *  (SP-1; QA round 2 N1: a lone EPS was edited on a 1 x 1 stand-in). RAW stays unsized. */
    private async probeItem(id: string): Promise<Size | null> {
        const it = this.state.items.find(x => x.id === id);
        if (!it || it.decoded || it.probed !== undefined) return it?.probed ?? null;
        const size = await probeServerFile(it.file, detectFormat(it.file));
        const fresh = this.state.items.find(x => x.id === id);
        if (fresh && !fresh.decoded) {
            const state = size ? (this.batch ? stateForMember(this.batch, this.batchOwner, { id, size, ownCrop: fresh.ownCrop }, this.state.cropPolicy) : initialState(size)) : null;
            await new Promise<void>(r => this.patchById(id, { probed: size, ...(state ? { state } : {}) }, r));
        }
        return size;
    }
    private browserCanShow(it: EditorItem): boolean {
        if (it.decodeFailed || (it.route === 'server' && it.probed && !withinBrowserBudget(it.probed))) return false;
        return it.route === 'browser' || (canDecodeInBrowser(detectFormat(it.file)) && it.file.size <= SERVER_PREVIEW_MAX_BYTES);
    }
    componentWillUnmount() {
        this.live = false; if (this.previewTimer) clearTimeout(this.previewTimer); if (this.thumbTimer) clearTimeout(this.thumbTimer); this.thumbGen++; if (this.settleTimer) clearTimeout(this.settleTimer); if (this.removedTimer) clearTimeout(this.removedTimer);
        if (typeof window !== 'undefined') {
            document.documentElement.removeAttribute('data-xc-editor'); // let the global anchor return
            cancelAnimationFrame(this.animId); if (this.resizeTimer) clearTimeout(this.resizeTimer);
            this.adTimers.forEach(clearTimeout); this.adTimers = []; this.adObserver?.disconnect();
            window.removeEventListener('keydown', this.onKey); window.removeEventListener('resize', this.onResize);
            window.removeEventListener('online', this.retryCodecs);
            document.removeEventListener('fullscreenchange', this.onFsChange);
            window.removeEventListener('scroll', this.onScroll, { capture: true } as any);
            document.removeEventListener('scroll', this.onScroll, { capture: true } as any);
        }
    }

    // Distraction-free full-window editor. Layout is driven by :fullscreen in the stylesheet; the
    // stage's ResizeObserver re-fits the preview when the element resizes.
    private toggleFullscreen = () => {
        const el = this.editorRef.current; if (!el) return;
        if (document.fullscreenElement) document.exitFullscreen?.();
        else el.requestFullscreen?.().catch(() => { /* denied by the browser; ignore */ });
    };
    private onFsChange = () => { if (this.live) this.setState({ fullscreen: !!document.fullscreenElement }); };

    // Proximity snap: when a scroll SETTLES (debounced — no dependency on the flaky scrollend event)
    // with the editor almost filling the viewport (within SNAP_TOL of the top or bottom edge), glide it
    // into a clean fill. Desktop only, never in fullscreen, and never while our own snap is animating.
    private settleTimer: ReturnType<typeof setTimeout> | null = null;
    private snapGuardUntil = 0;
    private onScroll = () => {
        if (!DESKTOP()) return;
        if (this.settleTimer) clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(this.maybeSnap, 140);
    };
    private maybeSnap = () => {
        const el = this.editorRef.current;
        if (!el || !DESKTOP() || this.state.fullscreen || document.fullscreenElement || Date.now() < this.snapGuardUntil) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.height > vh + 4) return; // taller than the screen — nothing to snap to
        if (r.top !== 0 && Math.abs(r.top) <= SNAP_TOL) this.snapEditor('start');
        else if (r.bottom !== vh && Math.abs(r.bottom - vh) <= SNAP_TOL) this.snapEditor('end');
    };

    // Scroll the editor to fill the viewport (top or bottom aligned). Native smooth scrollIntoView is a
    // no-op on this page's <body> scroller, so animate scrollTop ourselves — works on any scroller.
    private animId = 0;
    private getScroller(): HTMLElement {
        let el = this.editorRef.current?.parentElement as HTMLElement | null;
        while (el) { const oy = getComputedStyle(el).overflowY; if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2) return el; el = el.parentElement; }
        const b = document.body; if (b.scrollHeight > b.clientHeight + 2) return b;
        return (document.scrollingElement as HTMLElement) || document.documentElement;
    }
    private snapEditor(block: 'start' | 'end') {
        const el = this.editorRef.current; if (!el) return;
        this.snapGuardUntil = Date.now() + 700;
        const sc = this.getScroller();
        const r = el.getBoundingClientRect();
        const target = sc.scrollTop + (block === 'start' ? r.top : r.bottom - window.innerHeight);
        this.smoothScrollTo(sc, target);
    }
    private smoothScrollTo(sc: HTMLElement, target: number) {
        cancelAnimationFrame(this.animId);
        const start = sc.scrollTop, dist = target - start, dur = 280, t0 = performance.now();
        if (Math.abs(dist) < 1) return;
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { sc.scrollTop = target; return; }  // no glide for reduced motion (N6)
        const step = (now: number) => {
            const p = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - p, 3); // easeOutCubic
            sc.scrollTop = start + dist * e;
            if (p < 1 && this.live) this.animId = requestAnimationFrame(step);
        };
        this.animId = requestAnimationFrame(step);
    }

    componentDidUpdate(prevProps: EditorProps, prev: EditorState) {
        // IMAGEEDITOR-21: the double-click guard is for the same download only - another format, quality, photo or edit
        // is a new download at once
        const s = this.state;
        if (prev.format !== s.format || prev.quality !== s.quality || prev.selected !== s.selected || prev.past !== s.past || prev.future !== s.future || prev.cropPolicy !== s.cropPolicy) this.saveGuardUntil = 0;
        if (prevProps.files !== this.props.files) this.appendFiles(this.props.files);
        // owner 2026-10-10: the strip cards follow every edit (rotate, flip, crop, resize, own crop, undo, policy) - and a
        // photo just opened gets its card; a pass is debounced and runs off the preview's path (renderThumbs)
        if (prev.items !== s.items && thumbInputsChanged(prev.items, s.items)) this.scheduleThumbs();
        if (prev.selected !== this.state.selected) { const sel = this.state.selected; this.ensureDecoded(sel).then(() => { this.fitBox(); this.schedulePreview(true); }); }   // selection only changes what is drawn
        else if (prev.tool !== this.state.tool && this.state.tool === 'crop') this.schedulePreview(true);   // build the crop base
        else if (prev.zoom !== this.state.zoom || prev.view !== this.state.view) this.schedulePreview();    // re-render at the new screen resolution
    }

    // ------------------------------------------------------------ items
    private get current(): EditorItem { return this.state.items[this.state.selected]; }

    /** Add more / + add / drop / paste (power-2, critic-1): the new files join the open batch. Nothing is
     *  remounted: every edit, own crop, undo step, running download and upload stays; the new photos get
     *  the batch's edit like any other member. */
    private appendFiles(files: File[]) {
        const have = new Set(this.state.items.map(it => it.file));
        const add = files.filter(f => !have.has(f));
        if (!add.length) return;
        const base = this.state.items.length;
        const fresh = add.slice(0, Math.max(0, BATCH_LIMIT - base)).map((f, k) => {
            const it = newItem(f, base + k);
            if (this.props.bulk && this.server) it.route = this.server.routeFor(detectFormat(f), true);
            return it;
        });
        if (!fresh.length) return;
        this.setState(s => ({ items: [...s.items, ...fresh], outcome: '' }), async () => {
            for (const it of fresh) {
                if (!this.live) return;
                const i = this.state.items.findIndex(x => x.id === it.id); if (i < 0) continue;
                if (this.props.bulk && !this.browserCanShow(this.state.items[i])) await this.probeItem(it.id);
                else await this.ensureDecoded(i);
            }
            // J12-7: the stage does not stay on a file that could not be opened once a readable one was added
            if (this.live && this.current?.error) { const ok = this.state.items.findIndex(x => !x.error && (x.decoded || x.probed)); if (ok >= 0) this.setState({ selected: ok, preview: null }); }
            this.reconcileFormat();
        });
        this.props.onTelemetry?.('EDITOR_FILES_ADDED', { count: fresh.length, total: base + fresh.length });
    }
    /** The format the user picked by hand is theirs; a page default (crop-heic = HEIC) that some open file
     *  would not be written in falls back to "Same as original", so the Save-as list never names a
     *  format the button and the file won't be (regression-6). */
    private formatPicked = false;
    private reconcileFormat() {
        const f = this.state.format;
        if (this.formatPicked || f === 'original' || !this.codecs || !this.live) return;
        const known = this.state.items.filter(it => it.decoded || (this.props.bulk && it.route === 'server'));
        // J8-11 / J3-9: the page's format is a default for files OF that format; a JPG on /crop-tiff is not
        // silently made a TIFF (+743 %). ICO pages keep theirs: making an icon is why people open them.
        const src = (it: EditorItem): ImageFormat => it.decoded?.sourceFormat ?? detectFormat(it.file);
        if (known.some(it => this.outFormatFor(it) !== f || (f !== 'ico' && f !== 'png' && src(it) !== f) || (f === 'png' && src(it) !== 'eps' && src(it) !== 'png'))) this.setState({ format: 'original' });
    }
    private onFormat = (f: SaveFormat) => {
        // circle -> PNG is the inspector's automatic switch, not a pick (firsttime-9)
        if (this.circleSwitchPending) { this.circleSwitchPending = false; if (f !== this.state.format) this.formatBeforeCircle = this.state.format; }
        else { this.formatPicked = true; this.formatBeforeCircle = null; }
        // R2-save-as-format-quality-matrix-6: "Done: logo-500x300.jpg is ready." is about the file saved before;
        // once Save as changes, the line would name a file that is not the one Download now makes
        if (f !== this.state.format && /^(Done|Saved)/.test(this.state.outcome)) this.setState({ format: f, failureAck: null, outcome: '' });
        else this.setState({ format: f, failureAck: null });
    };
    private circleSwitchPending = false;
    private formatBeforeCircle: SaveFormat | null = null;
    /** Circle on: remember the Save-as so Rectangle can give it back if PNG came only from the circle. */
    private trackCircle(prev: EditState | null | undefined, next: EditState) {
        const was = prev ? cropView(prev).shape : 'rect'; const now = cropView(next).shape;
        if (was !== 'circle' && now === 'circle') { this.circleSwitchPending = true; setTimeout(() => { this.circleSwitchPending = false; }, 0); }
        else if (was === 'circle' && now !== 'circle' && this.formatBeforeCircle !== null) {
            const back = this.formatBeforeCircle; this.formatBeforeCircle = null;
            this.setState({ format: back });
        }
    }
    /** × on a thumb (C5). */
    private removeAt = (i: number) => { const it = this.state.items[i]; if (it) this.removeItem(it.id); };
    /** "Clear batch" (C5): every photo out, back to the empty page (the stage asks first). */
    private clearBatch = () => {
        this.runToken++;
        this.state.items.forEach(it => it.job?.cancel());
        this.props.onTelemetry?.('EDITOR_BATCH_CLEARED', { files: this.state.items.length });
        this.props.onChangeFiles();
    };

    /**
     * Decode photo i in the browser (routing ruling 2026-10-07: the browser goes first, flag on or off).
     * Bulk: a file whose header says it is over the browser budget (300 MP) goes to our servers undecoded; a
     * file the browser cannot decode (DecodeError other than `unreadable`) goes there too when its header is
     * readable (a real file the browser lacks a decoder for), else it is reported at once as unreadable -
     * never an endless "Opening…" or a 1 x 1 stand-in (J2-6, J9-2, J13-6, J16-5). A photo whose pixels were
     * let go (`released`, bounded memory) is decoded again; its edit, thumb and size stay.
     */
    private async ensureDecoded(i: number) {
        const it = this.state.items[i]; if (!it || it.error || !this.codecs) return;
        // verify round item 7: trimSources zeroes a canvas at once but marks it released only on the next commit;
        // a zeroed source counts as released, so it is decoded again instead of drawn (drawImage of a 0-size canvas)
        if (it.decoded && !it.released && alive(it.decoded.source)) return;
        if (this.props.bulk && !this.browserCanShow(it)) return;   // edited on our servers only; no browser decode
        const pending = this.decoding.get(it.id); if (pending) return pending;
        const run = this.decodeItem(it).finally(() => this.decoding.delete(it.id));
        this.decoding.set(it.id, run);
        return run;
    }
    private decoding = new Map<string, Promise<void>>();
    private async decodeItem(it: EditorItem) {
        const idx = () => this.state.items.findIndex(x => x.id === it.id);
        // a file over the budget is caught by its header; under 16 MB a 300 MP image would be a decompression bomb,
        // which the decoder refuses (DecodeError 'too-big') and the catch below sends to our servers
        if (this.props.bulk && this.server && !it.decoded && it.route === 'browser' && it.file.size > BUDGET_PROBE_BYTES) {
            const head = await probeServerFile(it.file, detectFormat(it.file)).catch(() => null);
            if (head && !withinBrowserBudget(head)) {
                await this.toServer(it, head, 'budget');
                return;
            }
        }
        try {
            const decoded = await this.codecs!.decodeImage(it.file);
            if (it.decoded && (it.released || !alive(it.decoded.source))) {   // pixels back for a photo already open: keep its edit and thumb
                await new Promise<void>(r => this.patchById(it.id, { decoded, released: false, mips: new Map() }, r));
                this.trimSources();
                return;
            }
            const size = { width: decoded.width, height: decoded.height };
            const state = this.batch ? stateForMember(this.batch, this.batchOwner, { id: it.id, size, ownCrop: it.ownCrop }, this.state.cropPolicy) : initialState(size);
            const thumb = await scaleTo(decoded.source, fitThumb({ width: decoded.width, height: decoded.height }));
            const i = idx(); if (i < 0) return;
            await this.patchItem(i, { decoded, state, thumb });
            if (decoded.animated) this.askGifMaker();
            if (i === this.state.selected) this.fitBox({ width: decoded.width, height: decoded.height });
            this.props.onTelemetry?.('EDITOR_FILE_OPENED', { format: decoded.sourceFormat, width: decoded.width, height: decoded.height, bytes: it.file.size, ...(this.props.bulk ? { route: 'browser' } : {}) });
            this.trimSources();
        } catch (e: any) {
            // bulk: a server-route file the browser cannot open is still fine — the edge decodes it
            if (this.props.bulk && it.route === 'server') return;
            // bulk: a file the browser cannot decode goes to our servers when it is a real image there (its header
            // gives a size, or a real PSD); bytes with no readable header and `unreadable` (moved, locked) never do
            const code = e?.code;
            if (this.props.bulk && this.server && code !== 'unreadable' && !it.released) {
                const fmt = detectFormat(it.file);
                const head = fmt !== 'unknown' && it.file.size > 0 ? await probeServerFile(it.file, fmt).catch(() => null) : null;
                if (head || fmt === 'eps' || fmt === 'raw' || await this.isRealPsd(it.file)) { await this.toServer(it, head, 'decode'); return; }
            }
            const message = friendlyFailure(it.file, await this.notAnImage(it.file, code) ?? (e?.message || 'This file could not be opened.'), !!this.props.bulk);
            const i = idx(); if (i < 0) return;
            await this.patchItem(i, this.props.bulk ? { error: message, status: 'failed', failure: { code: 'decode', message } } : { error: message });
            this.props.onTelemetry?.('EDITOR_FILE_FAILED', { name: it.file.name.split('.').pop(), reason: String(e?.message || e).slice(0, 80) });
        }
    }
    /** Routing ruling 2026-10-07: this file goes to our servers (over budget, or the browser could not decode it). */
    private async toServer(it: EditorItem, head: Size | null, why: 'budget' | 'decode') {
        await new Promise<void>(r => this.patchById(it.id, { route: 'server', ...(why === 'decode' ? { decodeFailed: true } : {}) }, r));
        await this.probeItem(it.id);
        this.props.onTelemetry?.('EDITOR_FILE_ROUTED', { route: 'server', why, format: detectFormat(it.file), ...(head ? { width: head.width, height: head.height } : {}) });
        if (this.current?.id === it.id) this.schedulePreview(true);
    }
    /** Bounded memory (J3-1/J3-3): let go of the decoded pixels of photos other than the selected one, largest
     *  first, until those kept fit RETAIN_PX. Photos being exported (`pinned`) are kept. */
    private pinned = new Set<string>();
    private trimSources() {
        if (!this.live) return;
        const sel = this.current?.id;
        const px = (x: EditorItem) => (x.decoded ? x.decoded.width * x.decoded.height : 0);
        const held = this.state.items.filter(x => x.decoded && !x.released && x.id !== sel && !this.pinned.has(x.id)).sort((a, b) => px(b) - px(a));
        let total = held.reduce((a, x) => a + px(x), 0);
        const drop = new Set<string>();
        for (const x of held) { if (total <= RETAIN_PX) break; drop.add(x.id); total -= px(x); }
        if (!drop.size) return;
        const free = (c: any) => { try { if (typeof c?.close === 'function') c.close(); else if (c) { c.width = 0; c.height = 0; } } catch { /* already gone */ } };
        this.state.items.forEach(x => { if (drop.has(x.id)) { free(x.decoded!.source); x.mips.forEach(free); } });
        this.setState(s => ({ items: s.items.map(x => drop.has(x.id) && x.decoded ? { ...x, released: true, mips: new Map() } : x) }));
    }
    /** IMAGEEDITOR-28: a file named like a photo whose bytes are no picture format at all (a text file saved as .jpg)
     *  is "not an image", not "may be damaged". Null = the decoder's own words stand (a real but broken image). */
    private async notAnImage(f: File, code: unknown): Promise<string | null> {
        // one file open: its own words; a batch keeps the decoder's (the failure list names each file)
        if (this.state.items.length !== 1 || code === 'unreadable' || f.size === 0 || !canDecodeInBrowser(detectFormat(f))) return null;
        try { return sniffFormat(await readHead(f)) === 'unknown' ? `${f.name} is not an image: there is no picture in it. Choose a photo (JPG, PNG, WebP, HEIC…).` : null; }
        catch { return null; }
    }
    private async isRealPsd(f: File): Promise<boolean> {
        if (detectFormat(f) !== 'psd' || f.size < 26) return false;
        try { const b = await readHead(f); return b[0] === 0x38 && b[1] === 0x42 && b[2] === 0x50 && b[3] === 0x53; }   // "8BPS"
        catch { return false; }
    }
    /** Resolves once React has committed the patch: callers that read `this.state.items` right after
     *  (the export loops) must see the decoded photo, or a file still decoding when Download was
     *  clicked is skipped / reported as unreadable (fix round 1b). */
    private patchItem(i: number, patch: Partial<EditorItem>): Promise<void> {
        if (!this.live) return Promise.resolve();
        return new Promise(r => this.setState(s => ({ items: s.items.map((it, j) => j === i ? { ...it, ...patch } : it) }), r));
    }

    // ------------------------------------------------------------ previews
    private schedulePreview(immediate = false) {
        if (this.previewTimer) clearTimeout(this.previewTimer);
        this.previewTimer = setTimeout(() => this.renderPreviews(), immediate ? 0 : 60);
    }
    private async renderPreviews() {
        const it = this.current; if (!it?.decoded || !it.state || it.released) {
            this.setState({ busy: it?.error || (this.props.bulk && it && !this.browserCanShow(it)) ? null : openingLabel(it?.file) });
            // a photo whose pixels were let go (bounded memory) is decoded again, then drawn
            if (it?.released) { const id = it.id; this.ensureDecoded(this.state.selected).then(() => { if (this.current?.id === id && !this.current.released) this.schedulePreview(true); }); }
            return;
        }
        const token = ++this.renderToken;
        try {
            const edge = PREVIEW_EDGE();
            const st = it.state;
            // Crop edits the image the ops BEFORE the current crop produced (additive model, 2026-09-06):
            // render that base as well while the Crop tool is open.
            const cropAt = currentIndex(st, 'crop'); const upTo = cropAt < 0 ? st.ops.length : cropAt;
            // Previews are drawn from the ORIGINAL at the screen's resolution for the current zoom (device
            // pixels), so a photo shrunk to 277 px never previews as a tiny canvas stretched across a
            // Retina screen (owner, 2026-09-06). `zoom` follows what the stage shows (Fit re-syncs it).
            const displayScale = Math.max(0.02, this.state.zoom || 1) * ((typeof window !== 'undefined' && window.devicePixelRatio) || 1);
            const opts = { maxEdge: edge, displayScale, mips: it.mips };
            const [p, sp, bp] = await Promise.all([
                renderPreview(it.decoded.source, st, opts),
                this.state.sourcePreview && this.state.sourcePreview.canvas.dataset.itemId === it.id ? Promise.resolve(this.state.sourcePreview) : renderPreview(it.decoded.source, initialState(st.source), opts),
                this.state.tool === 'crop' ? renderPreview(it.decoded.source, st, { ...opts, upTo }) : Promise.resolve(null),
            ]);
            if (token !== this.renderToken || !this.live) return;
            sp.canvas.dataset.itemId = it.id;
            this.setState({ preview: p, sourcePreview: sp, basePreview: bp, busy: null });
        } catch (e: any) {
            if (token === this.renderToken && this.live) this.setState({ busy: null, fatal: e?.message || 'The preview could not be drawn.' });
        }
    }

    // ------------------------------------------------------------ strip cards (owner 2026-10-10, model/thumbs)
    // Each card shows its file's effective edit - stateFor(it), the very state the export renders - drawn by the
    // render pipeline at ≤ THUMB_EDGE px. Debounced (edits fire at slider rate), one card per idle turn (the stage
    // preview is never waited on), selected photo first, cached per card by thumbKey; a pass that a newer one
    // supersedes stops at its next card, and a card drawn for an edit the file no longer has is dropped at commit.
    private scheduleThumbs() {
        const now = Date.now();
        if (!this.thumbAsked) this.thumbAsked = now;
        if (this.thumbTimer) clearTimeout(this.thumbTimer);
        const wait = Math.max(0, Math.min(THUMB_DEBOUNCE_MS, this.thumbAsked + THUMB_MAX_WAIT_MS - now));
        this.thumbTimer = setTimeout(() => { this.thumbTimer = null; this.thumbAsked = 0; void this.renderThumbs(); }, wait);
    }
    /** The card's render source: the photo's pixels while they are here (sharp, through its mips), else its ≤128 px
     *  source thumb standing for the photo's size - never a decode for a card. Null: nothing to draw from yet. */
    private stripSource(it: EditorItem): { src: Source; mips?: MipCache; sourceSize?: Size } | null {
        if (!it.decoded || !it.thumb || !alive(it.thumb)) return null;
        if (!it.released && alive(it.decoded.source)) return { src: it.decoded.source, mips: it.mips };
        return { src: it.thumb, sourceSize: { width: it.decoded.width, height: it.decoded.height } };
    }
    private async renderThumbs() {
        const gen = ++this.thumbGen;
        const sel = this.current?.id;
        const order = this.state.items.filter(x => x.id === sel).concat(this.state.items.filter(x => x.id !== sel));
        let drawn: Record<string, { key: string; canvas: HTMLCanvasElement }> = {}; let flushed = Date.now();
        const flush = () => { if (Object.keys(drawn).length) { this.commitStrips(drawn); drawn = {}; } flushed = Date.now(); };
        for (const x of order) {
            if (gen !== this.thumbGen || !this.live) return;
            const it = this.state.items.find(y => y.id === x.id);   // as it is now, not as the pass began
            if (!it || it.error || !it.decoded || !it.thumb) continue;
            const st = this.stateFor(it); const key = thumbKey(it.id, st);
            if (it.strip?.key === key) continue;
            try {
                if (!isEdited(st)) drawn[it.id] = { key, canvas: it.thumb };   // untouched: the source thumb is the card
                else {
                    const s = this.stripSource(it); if (!s) continue;
                    const canvas = await renderThumb(s.src, st, s);
                    if (gen !== this.thumbGen || !this.live) return;   // superseded while drawing: a newer pass redraws
                    drawn[it.id] = { key, canvas };
                }
            } catch { /* the card keeps its last picture; the next pass tries again */ }
            if (it.id === sel || Date.now() - flushed > THUMB_FLUSH_MS) flush();
            await idle();
        }
        flush();
    }
    /** Cards reach the strip only for the edit their file has NOW (one drawn for an edit since changed is stale: dropped). */
    private commitStrips(drawn: Record<string, { key: string; canvas: HTMLCanvasElement }>) {
        if (!this.live) return;
        this.setState(s => ({ items: s.items.map(x => { const d = drawn[x.id]; return d && thumbKey(x.id, this.stateFor(x)) === d.key ? { ...x, strip: d } : x; }) }));
    }

    // ------------------------------------------------------------ edits + history (undo / redo)
    // One history entry per user INTENT: a handle drag is one step (onDragging brackets it), and
    // rapid repeats — a slider, typing digits — coalesce within 400ms so undo steps back by action,
    // not by keystroke. Owner asked for undo/redo + Reset over the photo (2026-09-06).
    private dragging = false; private dragPushed = false; private lastPush = 0;
    private static readonly HISTORY_MAX = 60;
    private setEditState = (next: EditState) => {
        const i = this.state.selected; const now = Date.now();
        const push = !(this.dragging && this.dragPushed) && now - this.lastPush > 400;
        this.saveGuardUntil = 0;   // IMAGEEDITOR-21: an edit makes the next Download a new one
        if (this.dragging) this.dragPushed = true;
        if (push) this.lastPush = now;
        this.trackCircle(this.state.items[i]?.state, next);
        if (this.state.resetNote) this.setState({ resetNote: null });
        this.setState(s => {
            const before = this.snap(s.items);
            return { ...(push ? this.pushed(s, before) : { past: s.past, future: s.future }), items: this.withViews(s.items.map((it, j) => j === i ? { ...it, ...this.fold(it, next) } : it)) };
        }, () => this.schedulePreview());
    };
    /** The model as it is now (an undo point). */
    private snap(items: EditorItem[]): ModelSnap {
        const own: Record<string, OwnCrop | null> = {}; items.forEach(it => { own[it.id] = it.ownCrop; });
        return { batch: this.batch, owner: this.batchOwner, own };
    }
    private pushed(s: EditorState, before: ModelSnap): Pick<EditorState, 'past' | 'future'> {
        return { past: [...s.past, before].slice(-EditorComponent.HISTORY_MAX), future: [] };
    }
    /** Put a snapshot back: the batch + owner, and each photo's own crop (a photo added since keeps none). */
    private restore(items: EditorItem[], to: ModelSnap): EditorItem[] {
        this.batch = to.batch; this.batchOwner = to.owner;
        if (!this.batch) {
            // no batch yet: every photo's view is its original (viewOf keeps it.state while there is no batch)
            return this.withViews(items.map(it => ({ ...it, ownCrop: this.restoredOwn(it, to), state: it.state ? initialState(it.state.source) : it.state })));
        }
        return this.withViews(items.map(it => ({ ...it, ownCrop: this.restoredOwn(it, to) })));
    }
    /** R2-batch-edit-scope-and-handoff-i-5: "Only the selected photo" + 1:1, then Undo, is back to the batch - the take
     *  left an own crop that cuts nothing (whole photo, free ratio), so it is not brought back with its "own crop" tag. */
    private restoredOwn(it: EditorItem, to: ModelSnap): OwnCrop | null {
        const own = to.own[it.id] ?? null;
        const size = it.decoded ? { width: it.decoded.width, height: it.decoded.height } : (it.probed ?? null);
        // only when the step undone is the crop made on it (a Reset to batch undone brings the take back as it was)
        return own && !hasOwnCrop(own, size) && hasOwnCrop(it.ownCrop, size) ? null : own;
    }
    private onDragging = (d: boolean) => { this.dragging = d; this.dragPushed = false; if (d) this.lastPush = 0; };
    private undo = () => { if (this.state.resetNote) this.setState({ resetNote: null }); this.stepHistory('undo'); };
    private redo = () => this.stepHistory('redo');
    private stepHistory(dir: 'undo' | 'redo') {
        this.lastPush = 0;
        this.setState(s => {
            const now = this.snap(s.items);
            if (dir === 'undo') { if (!s.past.length) return null; return { items: this.restore(s.items, s.past[s.past.length - 1]), past: s.past.slice(0, -1), future: [now, ...s.future] }; }
            if (!s.future.length) return null; return { items: this.restore(s.items, s.future[0]), future: s.future.slice(1), past: [...s.past, now] };
        }, () => this.schedulePreview());
    }
    // Reset (all, or one tool) also returns the view to Fit: a zoom chosen for a small result would leave
    // the restored original spilling out of the box (owner, 2026-09-06).
    // QA round 4 OC4-2: on a photo with its own crop, Reset drops only that crop (the photo shows the batch
    // again); "Reset all" (onResetBatch) returns the batch and every own crop to the original.
    private resetAll = () => {
        const it = this.current; const st = it?.state; if (!it || !st) return;
        this.lastPush = 0;
        if (!it.ownCrop) { this.setEditState(resetAll(st)); this.setState({ view: 'fit', resetNote: 'Edits reset.' }); return; }
        this.setState(s => {
            const before = this.snap(s.items);
            const r = resetPhoto(this.batch, this.batchOwner, { ...this.member(it), size: this.member(it).size ?? { width: 0, height: 0 } }, s.cropPolicy);
            this.batch = r.batch; this.batchOwner = r.ownerId;
            return { ...this.pushed(s, before), view: 'fit', items: this.withViews(s.items.map(x => x.id === it.id ? { ...x, ownCrop: null } : x)) };
        }, () => this.schedulePreview());
    };
    private resetBatch = () => {
        const it = this.current; const st = it?.state; if (!it || !st) return;
        this.lastPush = 0;
        this.setState(s => {
            const before = this.snap(s.items);
            this.batch = initialState(isUnsized(this.member(it).size) ? { width: 0, height: 0 } : st.source); this.batchOwner = it.id;
            return { ...this.pushed(s, before), view: 'fit', resetNote: 'Every photo is back to the original.', items: this.withViews(s.items.map(x => (x.ownCrop ? { ...x, ownCrop: null } : x))) };
        }, () => this.schedulePreview());
    };
    private resetToolFit = (t: Tool) => { const st = this.current?.state; if (st) { this.lastPush = 0; this.setEditState(resetTool(st, t)); this.setState({ view: 'fit' }); } };
    private onTool = (t: Tool) => { this.setState({ tool: t }); this.props.onTelemetry?.('EDITOR_TOOL', { tool: t }); };
    private onKey = (e: KeyboardEvent) => {
        const t = e.target as HTMLElement | null; if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
        if (!(e.ctrlKey || e.metaKey)) return;
        const k = e.key.toLowerCase();
        if (k === 'z') { e.preventDefault(); if (e.shiftKey) this.redo(); else this.undo(); }
        else if (k === 'y') { e.preventDefault(); this.redo(); }
    };

    /** Phone only: choose the canvas box height ONCE per photo from its aspect (34–56vh) and keep it
     *  fixed while editing — a flat 46vh left 44% of the box empty for landscape photos (UX review M4)
     *  while the controls sat below the fold. Desktop flexes to fill the screen instead. */
    // ------------------------------------------------------------ floating ads (AdSense auto ads)
    // The site loads adsbygoogle.js, and auto ads float side rails on wide screens and an anchor at the
    // bottom on phones. Converter pages sit in a 994px column so the rails land in the margins; this
    // editor is full-bleed, so on prod they covered the photo, the inspector and the Download sheet
    // (owner, 2026-09-07). Rather than give up the screen unconditionally, measure whatever is actually
    // floating and inset the editor by exactly that much — with no ad (ad blocker, no fill) the editor
    // stays full-bleed. Ads arrive late and move, so re-measure for a while and on DOM changes.
    private adTimers: ReturnType<typeof setTimeout>[] = [];
    private adObserver: MutationObserver | null = null;
    private adGutters = { l: 0, r: 0, b: 0 };
    private static readonly AD_SELECTOR = 'iframe[src*="googlesyndication"], iframe[src*="doubleclick"], iframe[id^="aswift"], iframe[id^="google_ads"], .google-auto-placed, ins.adsbygoogle';
    private measureAds = () => {
        const el = this.editorRef.current; if (!el || !this.live || typeof window === 'undefined') return;
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth, vh = window.innerHeight;
        let l = 0, rt = 0, b = 0;
        document.querySelectorAll(EditorComponent.AD_SELECTOR).forEach(n => {
            // the floating box is the nearest fixed/sticky ancestor (Google wraps the iframe in one)
            let box: HTMLElement | null = n as HTMLElement;
            while (box && !/fixed|sticky/.test(getComputedStyle(box).position)) box = box.parentElement;
            if (!box) return;
            const a = box.getBoundingClientRect();
            if (a.width < 30 || a.height < 30) return;
            if (a.bottom <= r.top || a.top >= r.bottom || a.right <= r.left || a.left >= r.right) return;   // no overlap
            if (a.bottom > vh - 8 && a.width > vw * 0.5) { b = Math.max(b, Math.min(vh * 0.35, vh - a.top)); return; }   // bottom anchor
            if (a.left <= vw / 2) l = Math.max(l, Math.min(vw * 0.25, a.right - r.left));                                 // left rail
            else rt = Math.max(rt, Math.min(vw * 0.25, r.right - a.left));                                                // right rail
        });
        const g = { l: Math.round(Math.max(0, l)), r: Math.round(Math.max(0, rt)), b: Math.round(Math.max(0, b)) };
        if (g.l === this.adGutters.l && g.r === this.adGutters.r && g.b === this.adGutters.b) return;
        this.adGutters = g;
        el.style.setProperty('--ed-ad-l', `${g.l}px`); el.style.setProperty('--ed-ad-r', `${g.r}px`); el.style.setProperty('--ed-ad-b', `${g.b}px`);
        this.fitBox();
    };
    private watchAds() {
        if (typeof window === 'undefined') return;
        [300, 1200, 3000, 6000, 10000].forEach(t => this.adTimers.push(setTimeout(this.measureAds, t)));
        this.adObserver = new MutationObserver(() => { if (this.adTimers.length < 40) this.adTimers.push(setTimeout(this.measureAds, 250)); });
        this.adObserver.observe(document.body, { childList: true, subtree: false });
    }

    private resizeTimer: ReturnType<typeof setTimeout> | null = null;
    private fitBox = (size?: Size) => {
        if (typeof window === 'undefined' || !this.live) return;
        if (DESKTOP()) { if (this.state.boxHeight !== null) this.setState({ boxHeight: null }); return; }
        const d = size ?? this.current?.decoded; if (!d) return;
        // fitZoom leaves 36px of the box's width for the handles' touch zones (box padding 12px 18px)
        // fitZoom leaves 112px of the box (≈½ in each side) so a grip can be dragged OUTWARD (owner, 2026-09-06)
        const photoW = Math.max(80, (this.editorRef.current?.clientWidth || window.innerWidth) - 112);
        const imgH = photoW * (d.height / d.width);
        const h = Math.round(Math.min(0.6 * window.innerHeight, Math.max(0.36 * window.innerHeight, imgH + 112)));
        if (h !== this.state.boxHeight) this.setState({ boxHeight: h });
    };
    private onResize = () => { if (this.resizeTimer) clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(() => { this.fitBox(); this.measureAds(); }, 150); };

    // ------------------------------------------------------------ output info
    private effectiveFormat(): ImageFormat { return this.outFormatFor(this.current); }
    /** The format a file is written in: on our servers, what the edge writes (EPS stays EPS); in the
     *  browser, what the encoder can write. The Download button names exactly this (QA round 2 N1). */
    private outFormatFor(it: EditorItem | undefined): ImageFormat {
        const src: ImageFormat = it?.decoded?.sourceFormat ?? (this.props.bulk && it ? detectFormat(it.file) : 'jpg');
        if (this.isServerItem(it)) return this.server!.serverOutputFormat(src, this.state.format);
        const wanted: ImageFormat = this.state.format === 'original' ? src : this.state.format;
        return this.encFallback(wanted);
    }
    /** What the browser writes for `f`: the static fallback, then any run-time one already seen (J14-1). */
    private encFallback(f: ImageFormat): ImageFormat {
        const t = this.codecs ? this.codecs.encodeFallback(f) : f;
        return this.state.runtimeFallback?.[t] ?? t;
    }
    private isServerItem(it: EditorItem | undefined): boolean { return !!(this.props.bulk && it && it.route === 'server' && this.server); }
    /** The result `it` holds, if it is still the output of the current settings (SP-6: no stale tick). */
    private freshResult(it: EditorItem): boolean { return it.status === 'done' && !!it.result && it.resultKey === this.resultKey(it, this.masterItem()); }
    /** A batch's Output line (fix round 1, BU-6/SP-3): only the files that will be in the download; the
     *  format over all of them; the summed estimate, or none when a file's size is only known after our
     *  servers made it — never the selected photo's alone. */
    private batchOutput(): Pick<OutputInfo, 'files' | 'label' | 'estimate' | 'delta' | 'note' | 'detailNote'> {
        const ok = this.state.items.filter(x => !x.error && x.status !== 'failed');
        const outs = ok.map(x => {
            const src = x.decoded?.sourceFormat ?? detectFormat(x.file);
            const server = this.props.bulk && x.route === 'server' && this.server;
            const fmt: ImageFormat = server ? this.server!.serverOutputFormat(src, this.state.format)
                : this.encFallback(this.state.format === 'original' ? src : this.state.format);
            // C1: calibrated on the source's own bytes per pixel; server files too when their size is known (visual-5)
            const bytes = this.estimateFor(x, fmt);
            return { format: formatLabel(fmt), bytes };
        });
        const sum = batchOutputSummary(outs);
        const orig = ok.reduce((a, x) => a + x.file.size, 0);
        // IMAGEEDITOR-06: nothing changes in any file - the download is the originals, said from the first second
        // (no re-estimate of a file not decoded yet, never "+38 %")
        const untouched = ok.length > 0 && ok.every(x => this.memberUntouched(x));
        // NEW-4 / formats-9 (last mile 2): sizeDelta says "same" inside ±10 % - the estimate cannot sign a change that small
        const delta = sum.bytes != null ? sizeDelta(sum.bytes, orig) : null;
        // power-4: animated files lose their animation - said for the batch, not only on the selected thumb
        const anim = ok.filter(x => x.decoded?.animated).length;
        // J2-5: a multi-page TIFF in a batch keeps only its first page - said for the batch too
        const paged = ok.filter(x => (x.decoded?.pageCount ?? 0) > 1).length;
        // IMAGEEDITOR-20: a file that ends early is counted for the batch too, from the moment it is read
        const partial = ok.filter(x => x.decoded?.partial).length;
        // firsttime-10: the batch says what it WILL save (J15-4: future tense - nothing is saved yet); J3-9: only
        // the files that change get the new size in their name
        const changed = ok.filter(x => !this.memberUntouched(x)).length;
        const naming = !changed ? 'each file keeps its name' : changed === ok.length ? 'each file keeps its name with the new size added' : 'edited files get the new size added to their name, the others keep theirs';
        // IMAGEEDITOR-29-30b: one usable file is saved as itself (saveZips), so the line does not promise a ZIP
        const zipNote = ok.length === 1 ? 'Only this file can be saved, so it downloads on its own, not as a ZIP.' : ok.length ? `Downloads as ${this.zipName(ok.length)}; ${naming}.` : null;
        // untouched files are saved as their own bytes (animation kept): the line says what an edit would do
        const animText = untouched ? 'an edit keeps only the first frame.' : 'only the first frame is edited and saved.';
        const note = [anim ? `${anim} animated ${anim === 1 ? 'file' : 'files'} (GIF/APNG/WebP): ${animText}` : null,
            paged ? `${paged} multi-page ${paged === 1 ? 'file' : 'files'} (TIFF): ${untouched ? 'an edit keeps only the first page.' : 'only the first page is kept.'}` : null,
            partial ? `${partial === 1 ? '1 file looks' : `${partial} files look`} incomplete (${partial === 1 ? 'it ends' : 'they end'} early): the missing part will be blank.` : null].filter(Boolean).join(' ') || null;
        // phone-3 (last mile 2): the ZIP line is informational - its own field, so a phone can fold it behind "Details"
        if (untouched) return { files: ok.length, label: sum.label, estimate: `${formatBytes(orig)} · unchanged`, delta: null, note, detailNote: zipNote };
        return { files: ok.length, label: ok.length ? sum.label : 'nothing to save', estimate: sum.bytes != null ? `≈ ${formatBytes(sum.bytes)}` : '', delta, note, detailNote: zipNote };
    }
    private outputInfo(): EditorOutputInfo {
        const it = this.current;
        const many = this.state.items.length > 1;
        if (it && !it.decoded && (it.error || it.status === 'failed')) {
            // BU-8: the selected photo could not be read — no stand-in 1 x 1 size for it
            const fmt = this.effectiveFormat(); const ext = this.codecs ? this.codecs.extensionFor(fmt) : fmt;
            return { size: { width: 0, height: 0 }, format: fmt, extension: ext, estimate: '', delta: null, filename: it.file.name, note: null, unreadable: true, skipped: !!it.error, ...(many ? this.batchOutput() : {}) };
        }
        const info = this.photoOutputInfo();
        if (!many) return info;
        const b = this.batchOutput();
        // the batch note already counts the animated files; the selected file's own first-frame line would say it twice
        const own = info.note ? info.note.replace(FIRST_FRAME_RE, '').replace(/Only the first page is kept \(\d+ pages\)\.|This TIFF has \d+ pages: an edit here keeps only the first page\./, '').replace(INCOMPLETE_RE, '').replace(/\s{2,}/g, ' ').trim() || null : null;
        return { ...info, ...b, note: [own, b.note].filter(Boolean).join(' ') || null };
    }
    /** C1 estimate for one file's output; null when its pixel size is not known here (RAW). Untouched = its own bytes. */
    private estimateFor(x: EditorItem, fmt: ImageFormat, st: EditState = this.stateFor(x)): number | null {
        const src = this.member(x).size;
        // IMAGEEDITOR-06: an untouched file is its own bytes, whether or not its size is known yet
        if (this.memberUntouched(x)) return x.file.size;
        if (isUnsized(src)) return null;
        void primeEstimateSource(x.file);   // reads the JPEG header once (quality + subsampling) before the first edit
        return estimateOutputBytes({ size: savedSize(st, fmt), format: fmt, quality: this.state.quality, hasAlpha: this.outputHasAlpha(x, st, fmt), fineRotation: fineTurn(st),
            source: { bytes: x.file.size, width: src!.width, height: src!.height, format: x.decoded?.sourceFormat ?? detectFormat(x.file), file: x.file, bitsPerSample: x.decoded?.bitsPerSample } });
    }
    /** IMAGEEDITOR-05a: the output carries transparency - the source's own, a circle crop's corners, or the corners a
     *  fine turn leaves transparent - so the estimate uses the alpha model (a circle PNG of a JPG photo is RGBA).
     *  A format that cannot hold alpha (JPG, PPM: matted onto white) is estimated opaque. */
    private outputHasAlpha(x: EditorItem, st: EditState, fmt: ImageFormat): boolean {
        return !OPAQUE_OUT.includes(fmt) && this.carriesAlpha(x, st);
    }
    /** The edit's result has transparent pixels, whatever the format then does with them. */
    private carriesAlpha(x: EditorItem, st: EditState): boolean {
        if (x.decoded?.hasAlpha) return true;
        if (isApplied(st, 'crop') && cropView(st).shape === 'circle') return true;
        const r = st.ops.find(op => op.type === 'rotate') as { fineAngle: number; cornerFill: string } | undefined;
        return !!r && r.fineAngle !== 0 && r.cornerFill === 'transparent';
    }
    /** The actions beside the selected photo's notes: GIF Maker for an animated file (IMAGEEDITOR-09), Keep PNG when
     *  the chosen format drops transparency (R2-save-as-format-quality-matrix-7). */
    private noteActions(it: EditorItem | undefined, fmt: ImageFormat): OutputAction[] {
        const out: OutputAction[] = [];
        if (it?.decoded?.animated && this.state.gifMaker) out.push({ label: GIF_MAKER_ACTION, onClick: this.openGifMaker, testId: 'gif-maker-link' });
        if (it?.decoded && it.state && alphaNote(this.carriesAlpha(it, it.state), fmt) && OPAQUE_OUT.includes(fmt)) out.push({ label: 'Keep PNG', onClick: () => this.onFormat('png'), testId: 'keep-png' });
        return out;
    }
    private photoOutputInfo(): EditorOutputInfo {
        const it = this.current; const st = it?.state ?? initialState({ width: 1, height: 1 });
        const fmt = this.effectiveFormat(); const size = savedSize(st, fmt);   // C2: the size of the FILE (ICO: square, <= 256)
        const wanted: ImageFormat | null = this.state.format === 'original' ? (it?.decoded?.sourceFormat ?? null) : this.state.format;
        const ext = this.codecs ? this.codecs.extensionFor(fmt) : fmt;
        const est = it ? (this.estimateFor(it, fmt, st) ?? 0) : 0;
        const orig = it?.file.size ?? 0;
        // untouched + same format = the original file, byte for byte — say so instead of a scary "+1045%"
        if (it && this.isUntouched(st)) {
            // a server file whose size no header gives (RAW) stays "size read on our servers", never 1 x 1
            const unk = !!this.props.bulk && isUnsized(this.member(it).size);
            // IMAGEEDITOR-09: an animated file says at once what an edit does to it, with the way to keep the animation
            const actions = this.noteActions(it, fmt);
            // L6 follow-up (2026-10-11): the file handed back IS the original, so the Output line and the Download button name
            // its own format - "APNG", not the PNG the encoder would write (keepsOwnFormat: apng -> png is the only case
            // where fmt differs from the source). Nothing else is derived from this format while untouched.
            const own = it.decoded?.sourceFormat;
            return { size: unk ? { width: 0, height: 0 } : size, format: own && keepsOwnFormat(own, fmt) ? own : fmt, extension: ext, estimate: `${formatBytes(orig)} · unchanged`, delta: null, filename: it.file.name, note: it.decoded ? openNote(it.decoded) : null,
                ...(unk ? { sourceUnknown: true, sizeUnknown: true } : {}), ...(actions.length ? { actions } : {}) };
        }
        if (it && this.isServerItem(it)) return this.serverOutputInfo(it, st, fmt, ext);
        const delta = sizeDelta(est, orig);   // NEW-4 (last mile 2): "≈ same size" inside ±10 %, never a wrong-signed %
        let note: string | null = null;
        const failedHere = !!wanted && !!this.state.runtimeFallback?.[this.codecs ? this.codecs.encodeFallback(wanted) : wanted];
        if (wanted && wanted !== fmt) note = failedHere ? `This browser couldn't write ${formatLabel(wanted)}, so it is saved as ${formatLabel(fmt)}.` : `${formatLabel(wanted)} can't be written in the browser — saving as ${formatLabel(fmt)}.`;
        else if (fmt === 'ico' && Math.max(size.width, size.height) > 256) note = 'ICO files are limited to 256 px: the icon will be scaled to fit 256 × 256.';
        // J9-9: an ICO is square - a non-square image sits centred on a transparent square, said up front
        const drawn = outputSize(st);
        if (fmt === 'ico' && drawn.width !== drawn.height) note = [note, `Icons are square: the ${drawn.width} × ${drawn.height} image is centred on a transparent ${size.width} × ${size.height} square.`].filter(Boolean).join(' ');
        // IMAGEEDITOR-34: the favicon set the ICO holds (16, 32, 48 + the chosen size), before the save; an ICO opened
        // here says it in its own (decoder) note
        if (fmt === 'ico' && it?.decoded?.sourceFormat !== 'ico' && this.codecs?.icoEntrySizes) {
            const sizes = this.codecs.icoSizesNote(this.codecs.icoEntrySizes(Math.max(size.width, size.height)));
            if (sizes) note = [note, sizes].filter(Boolean).join(' ');
        }
        // power-13 / formats-5 (browser half): what of THIS file the saved one does not keep, whatever the output format
        const kept = it?.decoded ? keptNote(it.decoded, { format: fmt, size }) : null;
        // R2-save-as-format-quality-matrix-7: transparency the chosen format cannot hold is said before the export
        // (the output's own transparency: the source's, a circle's corners or a transparent fine turn's - R2 matrix-2)
        const alpha = it?.decoded ? alphaNote(this.carriesAlpha(it, st), fmt, !it.decoded.hasAlpha) : null;
        const actions = this.noteActions(it, fmt);
        return { size, format: fmt, extension: ext, estimate: `≈ ${formatBytes(est)}`, delta, filename: nameFor(it?.file.name ?? 'image', size, ext, it?.decoded?.sourceFormat), note: [note, alpha, kept].filter(Boolean).join(' ') || null,
            ...(actions.length ? { actions } : {}) };
    }

    /** A file edited on our servers, one or fifty alike: its real size (header probe, or the result our
     *  servers returned), the format the edge writes, no browser byte estimate. Never a 1 x 1 stand-in. */
    private serverOutputInfo(it: EditorItem, st: EditState, fmt: ImageFormat, ext: string): OutputInfo {
        const src = it.decoded?.sourceFormat ?? detectFormat(it.file);
        const sourceUnknown = isUnsized(this.member(it).size);
        const done = this.freshResult(it) && it.resultSize ? it.resultSize : null;
        const size = done ?? (sourceUnknown ? { width: 0, height: 0 } : savedSize(st, fmt));
        const wanted: ImageFormat = this.state.format === 'original' ? src : this.state.format;
        const note = wanted !== fmt ? `${formatLabel(wanted)} is saved as ${formatLabel(fmt)} by our servers.` : null;
        // J16-10: once our servers made the file, "Saved as" is its real name
        // recheck P3: "Saved as camera.jpg" was not the name written (camera-3881x2607.jpg, SP-5): until our servers
        // report the size, the name shows where it goes (camera-…x….jpg); after, it is the real name
        const filename = done && it.resultName ? it.resultName : size.width > 0 ? nameFor(it.file.name, size, ext, src)
            : nameFor(it.file.name, size, ext, src).replace(/(\.[^.]+)$/, '-…x…$1');
        return { size, format: fmt, extension: ext, estimate: '', delta: null, filename, note, sourceUnknown, sizeUnknown: !(size.width > 0) };
    }

    // ------------------------------------------------------------ export
    /** No edits and the same format → the original bytes ARE what the user wants (UX review D1: a
     *  28 KB JPG came back as 318 KB, "+1045%"). Also keeps an untouched GIF animated. Batches and
     *  format changes always go through the encoder. */
    private isUntouched(st: EditState, it: EditorItem | undefined = this.current): boolean {
        if (!it || it.ownCrop) return false;
        // J9-2 / J16-6: bytes the browser could not decode are unverified - they go through our servers, which
        // either make a real file or say it is unreadable; never handed back as "Done"
        if (it.decodeFailed) return false;
        const src = it.decoded?.sourceFormat ?? (this.props.bulk && it.route === 'server' ? detectFormat(it.file) : undefined);
        // the quality slider is an edit of its own on a lossy file (firsttime-3)
        const qualityKept = this.state.quality === DEFAULT_QUALITY || !src || !LOSSY.includes(src as ImageFormat);
        // and the file comes out in its own format (RAW -> JPG, EPS -> PNG, HEIC -> JPG in the browser are writes)
        return this.noRealEdit(st) && !!src && (this.state.format === 'original' || this.state.format === src) && keepsOwnFormat(src, this.outFormatFor(it)) && qualityKept;
    }
    /** No tool changes the pixels: nothing applied, or (J9-10) only a resize back to the source's own size. */
    private noRealEdit(st: EditState): boolean {
        if (!TOOL_LIST.some(t => isApplied(st, t))) return true;
        if (TOOL_LIST.some(t => t !== 'resize' && isApplied(st, t))) return false;
        const o = outputSize(st);
        return o.width === st.source.width && o.height === st.source.height && st.source.width > 1;
    }
    /** A batch member with nothing to do: its original bytes and name go into the download, exactly as a
     *  single untouched file does (firsttime-10). */
    private memberUntouched(it: EditorItem): boolean {
        if (it.error) return false;
        if (!it.decoded && !(this.props.bulk && it.route === 'server')) return this.undecodedUntouched(it);
        return this.isUntouched(this.stateFor(it), it);
    }
    /** IMAGEEDITOR-06: a file not decoded yet is untouched when the batch has no edit, it has no own crop, and it
     *  comes out in its own format at the default quality (its bytes go into the download as they are). */
    private undecodedUntouched(it: EditorItem): boolean {
        if (it.decodeFailed || it.ownCrop || (this.batch && TOOL_LIST.some(t => isApplied(this.batch as EditState, t)))) return false;
        const src = detectFormat(it.file);
        if (src === 'unknown') return false;
        const qualityKept = this.state.quality === DEFAULT_QUALITY || !LOSSY.includes(src);
        return (this.state.format === 'original' || this.state.format === src) && keepsOwnFormat(src, this.encFallback(src)) && qualityKept;
    }
    /** Phones: the share sheet is how a photo reaches Photos / WhatsApp / Instagram (U7). Falls back
     *  to a download if the browser declines (e.g. the tap's activation expired during a slow encode). */
    private async shareFile(f: File) {
        try { await (navigator as any).share({ files: [f], title: f.name }); this.handedOff = true; }
        catch (e: any) { if (e?.name !== 'AbortError') { const fs: any = await EditorComponent.loaders.fileSaver(); this.trackedSave(fs.saveAs || fs.default?.saveAs || fs.default)(f, f.name); } }
    }
    /** IMAGEEDITOR-38: a phone batch goes to the share sheet in one go ("Save N images" to Photos). A browser that
     *  cannot share that many files saves them one by one instead; a cancelled sheet saves nothing.
     *  review10 loop-review: `shared` is false when the files were saved one by one instead (e.g. NotAllowedError after
     *  a long encode expired the tap), so the outcome line says what really happened. */
    private async shareFiles(entries: { name: string; blob: Blob }[], saveAs: (blob: Blob, name: string) => void): Promise<{ names: string[]; shared: boolean }> {
        const files = entries.map(e => new File([e.blob], e.name, { type: e.blob.type }));
        const nav: any = navigator;
        if (nav.share && (!nav.canShare || nav.canShare({ files }))) {
            try { await nav.share({ files }); this.handedOff = true; return { names: entries.map(e => e.name), shared: true }; }
            catch (e: any) { if (e?.name === 'AbortError') return { names: [], shared: true }; }
        }
        const n = await saveSequentially(entries, saveAs);
        return { names: entries.slice(0, n).map(e => e.name), shared: false };
    }
    /** IMAGEEDITOR-21: a Download is running (set at the click, before any await), and the time until the next one is taken. */
    private exportLock = false;
    private saveGuardUntil = 0;
    private saveGuardMode: 'single' | 'zip' | 'each' | 'share' | 'shareAll' | null = null;
    /** IMAGEEDITOR-21: set when the current run handed a file to the browser (a download, a share sheet that completed). */
    private handedOff = false;
    private trackedSave(save: (blob: Blob, name: string) => void): (blob: Blob, name: string) => void {
        return (blob, name) => { save(blob, name); this.handedOff = true; };
    }
    private onExport = async (mode: 'single' | 'zip' | 'each' | 'share' | 'shareAll') => {
        if (!this.codecs || this.state.exporting) return;
        // IMAGEEDITOR-21: a double-click saves one copy - clicks while one is being made, and for SAVE_GUARD_MS after, are ignored
        // review10 (IMAGEEDITOR-21 follow-up): the guard is per button - "Download one by one" right after a ZIP is a
        // different download, not a double-click
        if (this.exportLock || (mode === this.saveGuardMode && Date.now() < this.saveGuardUntil)) return;
        this.exportLock = true;
        // the guard is armed only when a save was handed to the browser: a failed or empty run can be retried at once
        this.handedOff = false;
        try { await this.runExport(mode); } finally { this.exportLock = false; if (this.handedOff) { this.saveGuardUntil = Date.now() + SAVE_GUARD_MS; this.saveGuardMode = mode; } }
    };
    private runExport = async (mode: 'single' | 'zip' | 'each' | 'share' | 'shareAll') => {
        if (!this.codecs || this.state.exporting) return;
        const one = mode === 'single' || mode === 'share';
        if (one && this.current?.error) return;   // N4: a file the browser skipped has nothing to save (the button is disabled too)
        // a single file is a batch of one: a server-route file takes the batch's server path (unless it is untouched)
        const untouched = one && !!this.current?.state && this.isUntouched(this.current.state);
        if (this.props.bulk && (mode === 'zip' || mode === 'each' || mode === 'shareAll' || (this.current?.route === 'server' && !untouched))) return this.exportBulk(mode);
        // file-saver ships CommonJS: the dynamic-import namespace exposes it only as `default`
        // (a function that also has `.saveAs`), not as a named `saveAs`. Destructuring `{ saveAs }`
        // yields undefined → "t is not a function" at call time (caught on QA 2026-09-06).
        const fs: any = await EditorComponent.loaders.fileSaver();
        const saveAs = this.trackedSave(fs.saveAs || fs.default?.saveAs || fs.default);
        const items = this.state.items.filter(it => !it.error);
        const master = this.current.state ?? this.stateFor(this.current);   // the selected photo may still be decoding
        const fmtWanted = this.state.format;
        const targets = one ? [this.current] : items;
        const total = one ? 1 : this.state.items.length;
        this.setState({ exporting: { done: 0, total: targets.length, label: 'Preparing…' }, outcome: '' });
        this.props.onTelemetry?.('EDITOR_EXPORT_START', { mode, files: targets.length, format: fmtWanted, tools: (['resize', 'crop', 'rotate', 'flip'] as Tool[]).filter(t => isApplied(master, t)) });
        const entries: { name: string; blob: Blob; source?: string }[] = [];   // source: formats-13 (stem-<srcext> names)
        // J11-4 / J16-10: every file left out is named, with why - on screen, not only for screen readers
        const left: { name: string; reason: string }[] = one ? [] : this.state.items.filter(it => it.error).map(it => ({ name: it.file.name, reason: 'could not be read' }));
        try {
            if (one && this.isUntouched(master)) {
                const f = this.current.file;
                if (mode === 'share') await this.shareFile(f); else saveAs(f, f.name);
                this.setState({ outcome: `Done: ${f.name} is ready.` });
                this.props.onTelemetry?.('EDITOR_EXPORT_DONE', { mode, files: 1, passthrough: true });
                return;
            }
            // Bounded memory (J3-1/J3-3): one file at a time - decode, render, encode, hand off, let go - and a
            // file that fails (even out of memory) is left out and named; the others are still saved
            for (let i = 0; i < targets.length; i++) {
                const it = targets[i];
                this.pinned.add(it.id);
                try {
                    // by id: a photo that finished decoding meanwhile is a new object (indexOf would be -1)
                    await this.ensureDecoded(this.state.items.findIndex(x => x.id === it.id));
                    const fresh = this.state.items.find(x => x.id === it.id);
                    if (!fresh?.decoded || fresh.released) { if (!one && fresh) left.push({ name: fresh.file.name, reason: 'could not be read' }); continue; }
                    if (!one && this.memberUntouched(fresh)) { entries.push({ name: fresh.file.name, blob: fresh.file, source: fresh.file.name }); continue; }
                    entries.push(await this.encodeItem(fresh, fmtWanted));
                } catch (e: any) {
                    if (one) throw e;
                    left.push({ name: it.file.name, reason: OOM_RE.test(String(e?.message || e)) ? OOM_TEXT : 'could not be saved' });
                    this.props.onTelemetry?.('EDITOR_FILE_FAILED', { name: it.file.name.split('.').pop(), reason: String(e?.message || e).slice(0, 80), phase: 'export' });
                } finally {
                    this.pinned.delete(it.id); this.trimSources();
                    this.setState({ exporting: { done: i + 1, total: targets.length, label: one ? 'Preparing…' : 'Preparing' } });
                }
            }
            // regression-3: nothing could be read - say so, never an empty ZIP
            if (!one && !entries.length) {
                const oom = left.filter(l => l.reason !== 'could not be read');
                this.setState({ fatal: oom.length ? `Nothing was saved. ${this.leftText(left)}` : nothingToSave(this.unreadableNames()), outcome: '' });
                this.props.onTelemetry?.('EDITOR_EXPORT_EMPTY', { mode });
                return;
            }
            let saved: string[] = [];
            if (mode === 'single' && entries[0]) { saveAs(entries[0].blob, entries[0].name); saved = [entries[0].name]; }
            if (mode === 'share' && entries[0]) { await this.shareFile(new File([entries[0].blob], entries[0].name, { type: entries[0].blob.type })); saved = [entries[0].name]; }
            if (mode === 'each') { const n = await saveSequentially(entries, saveAs); saved = entries.slice(0, n).map(e => e.name); }   // BU-5: back-to-back saves stop at 10
            let doneMode: string = mode;
            if (mode === 'shareAll') { const r = await this.shareFiles(entries, saveAs); saved = r.names; if (!r.shared) doneMode = 'each'; }
            if (mode === 'zip') saved = await this.saveZips(entries, saveAs, targets.length);
            this.setState({ outcome: this.doneText(doneMode, saved, entries.length, total, left) });
            if (left.length) this.props.onTelemetry?.('EDITOR_EXPORT_PARTIAL', { mode, ok: entries.length, failed: left.length });
            this.props.onTelemetry?.('EDITOR_EXPORT_DONE', { mode, files: entries.length || targets.length });
        } catch (e: any) {
            const msg = OOM_RE.test(String(e?.message || e)) ? `Not saved: ${OOM_TEXT}. Close other tabs or pick a smaller size, then try again.` : (e?.message || 'The file could not be saved.');
            this.setState({ fatal: msg, outcome: '' });
            this.props.onTelemetry?.('EDITOR_EXPORT_FAILED', { reason: String(e?.message || e).slice(0, 80) });
        } finally {
            this.setState({ exporting: null });
        }
    };
    /** One photo through the browser encoder; the output canvas is let go right after (bounded memory). */
    private async encodeItem(fresh: EditorItem, fmtWanted: SaveFormat): Promise<{ name: string; blob: Blob; source: string }> {
        const d = fresh.decoded!;
        const st = this.stateFor(fresh);
        let canvas = await renderOutput(d.source, st, fresh.mips);   // one high-quality resample from the original
        const wanted: ImageFormat = fmtWanted === 'original' ? d.sourceFormat : fmtWanted;
        const fmt = this.encFallback(wanted);
        if (fmt === 'ico' && Math.max(canvas.width, canvas.height) > 256) { const k = 256 / Math.max(canvas.width, canvas.height); canvas = await scaleTo(canvas, { width: Math.max(1, Math.round(canvas.width * k)), height: Math.max(1, Math.round(canvas.height * k)) }); }
        // geometry-7: keep the source's print resolution; IMAGEEDITOR-10 (J13-7): a JPG keeps the source's EXIF (date taken,
        // camera, GPS), Orientation already reset to 1 by the decoder since the pixels are upright
        const res = await this.codecs!.encodeCanvas(canvas, fmt, { quality: this.state.quality, dpi: d.dpi, ...(d.exif ? { exif: d.exif } : {}) });
        // J14-1: the encoder fell back at run time (e.g. the AVIF module did not load): from now on the page names what was written
        if (res.format && res.format !== fmt) await new Promise<void>(r => this.setState(s => ({ runtimeFallback: { ...s.runtimeFallback, [fmt]: res.format } }), r));
        // a big output canvas is let go now, not at the next GC (canvas memory is outside the JS heap)
        if (canvas !== (d.source as unknown) && canvas.width * canvas.height > 4e6) { try { (canvas as any).width = 0; (canvas as any).height = 0; } catch { /* not a canvas */ } }
        // C2: ICO names its real square size; ruling 6: same format keeps the source's extension spelling
        return { name: nameFor(fresh.file.name, savedSize(st, res.extension), res.extension, d.sourceFormat), blob: res.blob, source: fresh.file.name };
    }
    /** The ZIP, in parts of at most 1.5 GB (J3-1/J3-3), saved one after another; returns the names handed to the browser. */
    private async saveZips(entries: { name: string; blob: Blob; source?: string }[], saveAs: (b: Blob, n: string) => void, total: number): Promise<string[]> {
        // IMAGEEDITOR-29-30b: only one file came out - it is saved as itself, never as a 1-file ZIP
        if (entries.length === 1) { saveAs(entries[0].blob, entries[0].name); return [entries[0].name]; }
        const unique = uniqueNames(entries.map(e => e.name), entries.map(e => e.source));
        const parts = splitForZip(entries.map((e, i) => ({ ...e, name: unique[i] })));
        const base = this.zipName(entries.length);
        const names: string[] = [];
        for (let k = 0; k < parts.length; k++) {
            if (k) await new Promise(r => setTimeout(r, DOWNLOAD_GAP_MS));
            this.setState({ exporting: { done: total, total, label: parts.length > 1 ? `Zipping part ${k + 1} of ${parts.length}` : 'Zipping' } });
            const name = zipPartName(base, k, parts.length);
            saveAs(await zipEntries(parts[k]), name);
            names.push(name);
        }
        return names;
    }
    /** "a.tif (could not be read), b.jpg (your browser ran out of memory for this file)". */
    private leftText(left: { name: string; reason: string }[]): string {
        if (!left.length) return '';
        const shown = left.length > 5 ? [...left.slice(0, 4).map(l => `${l.name} (${l.reason})`), `${left.length - 4} more`] : left.map(l => `${l.name} (${l.reason})`);
        return `Not included: ${shown.join(', ')}.`;
    }
    /** The visible outcome line (E1 .outcome; J6-6, J8-10, J11-11): written only after the save was really
     *  handed to the browser - never after Cancel or a failure (J3-4). */
    private doneText(mode: string, saved: string[], made: number, total: number, left: { name: string; reason: string }[]): string {
        if (!saved.length) return left.length ? `Nothing was saved. ${this.leftText(left)}` : '';
        const files = `${made} ${made === 1 ? 'file' : 'files'}`;
        const head = mode === 'zip' ? (made === 1 ? `Done: ${saved[0]} is ready.` : saved.length === 1 ? `Done: ${saved[0]} is ready (${files}).` : `Done: ${saved.length} ZIP parts are ready (${files}): ${saved.join(', ')}.`)
            : mode === 'each' ? eachDoneText(saved.length)
            : mode === 'shareAll' ? (saved.length === 1 ? `Done: ${saved[0]} is shared.` : `Done: ${saved.length} files are shared.`)
            : `Done: ${saved[0]} is ready.`;
        return left.length ? `${head} Saved ${made} of ${total}. ${this.leftText(left)}` : head;
    }

    // ------------------------------------------------------------ bulk batch states (imageEditorBulk, PLAN C5)
    private runToken = 0;
    /** Files of the current run already handed to the browser ("one by one"), for the Cancel line. */
    private savedInRun = 0;
    private replaceRef = createRef<HTMLInputElement>();
    private replaceId: string | null = null;

    // ---- the batch owns the edits (fix round 1, BU-1/BU-2; ungated: BU-2 was live with the flag off).
    // `batch` = ONE EditState drawn on photo `batchOwner` (the last photo edited). Every photo's state is
    // a view of it (model/batch stateForMember); an own crop is an override on one photo. Selecting a
    // thumbnail only changes which view is shown.
    private batch: EditState | null = null;
    private batchOwner: string | null = null;
    private member(it: EditorItem): BatchMember {
        return { id: it.id, size: it.decoded ? { width: it.decoded.width, height: it.decoded.height } : (it.probed ?? null), ownCrop: it.ownCrop };
    }
    /** An edit on photo `it` -> the model (batch, owner, its own crop) updated; the views follow (withViews). */
    private fold(it: EditorItem, next: EditState): Partial<EditorItem> {
        // a photo the browser never opened (EPS/RAW, all-server batch): the inspector draws on a stand-in
        // 1 x 1 source; the batch keeps 0 x 0 so no pixel box is ever derived from it (model/batch isUnsized)
        const size = this.member(it).size ?? { width: 0, height: 0 };
        const forBatch = isUnsized(this.member(it).size) ? { ...next, source: size } : next;
        const m = { ...this.member(it), size };
        const f = foldEdit(this.batch, this.batchOwner, m, forBatch, this.state.cropPolicy);
        this.batch = f.batch; this.batchOwner = f.ownerId;
        return it.ownCrop ? { ownCrop: f.ownCrop } : {};
    }
    /** What photo `it` shows: a pure function of the model; a photo not opened yet keeps null. */
    private viewOf(it: EditorItem, policy: CropPolicy = this.state.cropPolicy): EditState | null {
        // a photo not opened yet has no view, except one edited on our servers only (EPS/RAW): it shows the batch
        if (it.error || (!it.state && !(this.props.bulk && this.batch && !it.decoded && !this.browserCanShow(it)))) return it.state;
        if (!this.batch && !it.ownCrop) return it.state;
        const m = this.member(it);
        const v = this.batch ? stateForMember(this.batch, this.batchOwner, m, policy) : stateForMember(initialState(m.size ?? (it.state as EditState).source), m.id, m, policy);
        // not opened here: the controls still show (and edit) the batch, on the stand-in 1 x 1 source
        return isUnsized(v.source) ? { ...v, source: { width: 1, height: 1 } } : v;
    }
    /** Every photo's view re-derived after the model changed (an edit, undo, own crop, crop policy). */
    private withViews(items: EditorItem[], policy?: CropPolicy): EditorItem[] {
        return items.map(it => { const v = this.viewOf(it, policy); return v === it.state ? it : { ...it, state: v }; });
    }
    private stateFor(it: EditorItem, _master?: unknown): EditState {
        const b = this.batch ?? initialState(it.decoded ? { width: it.decoded.width, height: it.decoded.height } : { width: 0, height: 0 });
        return exportStateForMember(b, this.batch ? this.batchOwner : null, this.member(it), this.state.cropPolicy);
    }
    private masterItem(): EditorItem | undefined { return this.current; }
    private zipName(n: number): string {
        // J15-4: own crops name the ZIP "cropped" only when every file has one
        const ok = this.state.items.filter(x => !x.error);
        const allOwn = ok.length > 0 && ok.every(x => x.ownCrop);
        const b = this.batch; return zipNameFor(TOOL_LIST.filter(t => (b && isApplied(b, t)) || (t === 'crop' && allOwn)), n);
    }
    private patchById(id: string, patch: Partial<EditorItem>, done?: () => void) {
        if (!this.live) { done?.(); return; }
        this.setState(s => ({ items: s.items.map(it => it.id === id ? { ...it, ...patch } : it) }), done);
    }
    private onOwnCrop = () => {
        const it = this.current; if (!it?.state || it.ownCrop) return;
        this.lastPush = 0;
        this.setState(s => {
            const before = this.snap(s.items);
            const t = takeOwnCrop(this.batch, this.batchOwner, this.member(it), s.cropPolicy);
            this.batch = t.batch;
            return { ...this.pushed(s, before), items: this.withViews(s.items.map(x => x.id === it.id ? { ...x, ownCrop: t.ownCrop } : x)) };
        }, () => this.schedulePreview());
        this.props.onTelemetry?.('EDITOR_OWN_CROP', {});
    };
    private onCropPolicy = (p: CropPolicy) => {
        const id = this.current?.id;
        this.setState(s => ({ cropPolicy: p, ...(s.items.some(x => x.id === id && x.ownCrop) ? this.pushed(s, this.snap(s.items)) : { past: s.past, future: s.future }),
            items: this.withViews(s.items.map(x => x.id === id && x.ownCrop ? { ...x, ownCrop: null } : x), p) }), () => this.schedulePreview());
    };

    private async processBrowser(it: EditorItem, master: EditorItem | undefined): Promise<RunResult> {
        this.pinned.add(it.id);
        try {
            await this.ensureDecoded(this.state.items.findIndex(x => x.id === it.id));
            const fresh = this.state.items.find(x => x.id === it.id);
            // routing ruling: the browser could not decode it after all - our servers take it, in this same run
            if (fresh && fresh.route === 'server' && !fresh.error) return await this.processServer(fresh, master, this.runToken);
            if (!fresh?.decoded || fresh.released) throw Object.assign(new Error(fresh?.error || `${it.file.name} could not be opened.`), { code: 'decode' });
            if (this.memberUntouched(fresh)) return { name: fresh.file.name, blob: fresh.file };
            try {
                void master;
                const r = await this.encodeItem(fresh, this.state.format);
                return { name: r.name, blob: r.blob };
            } catch (e: any) {
                const oom = OOM_RE.test(String(e?.message || e));
                                // verify round item 7: a browser API's raw text ("Failed to execute 'drawImage'…") is not shown
                const raw = /Failed to execute|CanvasRenderingContext2D|DOMException/i.test(String(e?.message || ''));
                throw Object.assign(new Error(oom ? `${it.file.name}: ${OOM_TEXT}. Close other tabs or pick a smaller size, then try again.` : (!raw && e?.message) || `${it.file.name} could not be saved. Try again.`), { code: 'encode' });
            }
        } finally { this.pinned.delete(it.id); this.trimSources(); }
    }
    private async processServer(it: EditorItem, master: EditorItem | undefined, token: number): Promise<RunResult> {
        const srv = this.server ?? (this.server = await EditorComponent.loaders.server());
        if (!it.decoded) await this.probeItem(it.id);
        const fresh = this.state.items.find(x => x.id === it.id) ?? it;
        if (this.memberUntouched(fresh)) return { name: fresh.file.name, blob: fresh.file };   // nothing to do: no upload
        const sourceFormat = fresh.decoded?.sourceFormat ?? detectFormat(fresh.file);
        if (fresh.uploaded != null) this.patchById(it.id, { sent: true });
        const job = srv.runServerEdit({
            file: fresh.file, state: this.stateFor(fresh, master), sourceFormat, outputFormat: srv.serverOutputFormat(sourceFormat, this.state.format),
            quality: this.state.quality, pageSlug: this.props.landing.slug,
            ...(fresh.uploaded != null ? { upload: fresh.uploaded } : {}),   // SP-7: the bytes are on the edge already
        }, p => {
            // bytes have left the browser once the upload moved or a later phase began
            const sent = p.phase === 'processing' || p.phase === 'downloading' || (p.phase === 'uploading' && (p.fraction ?? 0) > 0);
            if (token === this.runToken) this.patchById(it.id, { progress: p, ...(sent ? { sent: true } : {}) });
            else if (sent) this.patchById(it.id, { sent: true });
        });
        this.patchById(it.id, { job });
        const r = await job.result;
        this.patchById(it.id, { sent: true });
        return { name: r.filename, blob: r.blob, size: r.size ?? null, upload: r.upload };
    }

    /** Bulk export: browser files one at a time, server files SERVER_CONCURRENCY at a time, both at once.
     *  One bad file never blocks the rest: with a failure the ZIP waits for "Download the N" (or Replace /
     *  Remove); `partial` = that button (skips files already failed and saves what succeeded). */
    private exportBulk = async (mode: 'single' | 'zip' | 'each' | 'share' | 'shareAll', partial = false) => {
        const token = ++this.runToken;
        this.savedInRun = 0;
        const master = this.masterItem();
        const all = mode === 'single' || mode === 'share' ? [this.current] : this.state.items;
        const targets = all.filter(it => !it.error && !(partial && it.status === 'failed'));
        // regression-3: every file failed to open - a message, never a dead click or an empty ZIP
        if (!targets.length) { this.setState({ fatal: nothingToSave(this.unreadableNames()) }); this.props.onTelemetry?.('EDITOR_EXPORT_EMPTY', { mode, bulk: true }); return; }
        // A file already done with these exact settings is not run again (review BATCH-UX #1): its cached
        // result goes straight into the ZIP — no re-upload, no second counted download, no re-encode.
        const results = new Map<string, RunResult>();
        for (const it of targets) {
            if (it.status === 'done' && it.result && it.resultName && it.resultKey === this.resultKey(it, master)) results.set(it.id, { name: it.resultName, blob: it.result });
        }
        const todo = targets.filter(it => !results.has(it.id));
        const ids = new Set(todo.map(it => it.id));
        const startedAt = Date.now();
        // visual-7: one count over the whole batch (the chip's number); files that cannot be opened count as done
        const total = all.length; const pre = all.length - targets.length;
        this.setState(s => ({
            fatal: null, outcome: '',
            preparing: { done: pre + results.size, total, startedAt },
            exporting: { done: pre + results.size, total, label: 'Preparing' },
            items: s.items.map(it => ids.has(it.id) ? { ...it, status: 'queued', failure: null, result: null, resultName: null, resultKey: null, progress: null, job: null } : it),
        }));
        let done = pre + results.size;
        this.props.onTelemetry?.('EDITOR_EXPORT_START', { mode, files: targets.length, format: this.state.format, server: todo.filter(t => t.route === 'server').length, reused: results.size, bulk: true });
        const failed: string[] = [];
        const offline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
        const task = (it: EditorItem) => async () => {
            if (token !== this.runToken) return;
            this.patchById(it.id, { status: 'preparing', startedAt: Date.now() });
            try {
                const r = it.route === 'server' ? await this.processServer(it, master, token) : await this.processBrowser(it, master);
                if (token !== this.runToken) return;
                results.set(it.id, r);
                const fresh = this.state.items.find(x => x.id === it.id) ?? it;
                this.patchById(it.id, { status: 'done', result: r.blob, resultName: r.name, resultKey: this.resultKey(fresh, master), resultSize: r.size ?? null, job: null, progress: null,
                    ...(r.upload != null ? { uploaded: r.upload } : {}) });
            } catch (e: any) {
                if (token !== this.runToken) return;
                failed.push(it.id);
                const raw = offline() && it.route === 'server' ? OFFLINE : (e?.userMessage || e?.message || 'This file could not be processed.');
                const message = friendlyFailure(it.file, raw, true);
                const retryAfterSec = e?.code === 'rate_limited' ? waitOf(e) : undefined;
                this.patchById(it.id, { status: 'failed', job: null, progress: null, failure: { code: e?.code || 'failed', message: message.includes(it.file.name) ? message : `${it.file.name}: ${message}`, ...(retryAfterSec ? { retryAfterSec } : {}) } });
            }
            done++;
            if (token === this.runToken) this.setState({ preparing: { done, total, startedAt }, exporting: { done, total, label: 'Preparing' } });
        };
        const cancelled = () => token !== this.runToken;
        try {
            // critic-3: a chunk that cannot load (offline) ends in a message, not an unhandled rejection
            const fs: any = await EditorComponent.loaders.fileSaver();
            const saveAs = this.trackedSave(fs.saveAs || fs.default?.saveAs || fs.default);
            await Promise.all([
                runPool(todo.filter(t => t.route !== 'server').map(task), 1, cancelled),
                runPool(todo.filter(t => t.route === 'server').map(task), SERVER_CONCURRENCY, cancelled),
            ]);
            if (token !== this.runToken) return;
            const entries = targets.filter(t => results.has(t.id)).map(t => ({ ...results.get(t.id)!, source: t.file.name }));   // formats-13
            // a11y-10: a run with a failed file ends in words too (a clean run: the inspector's "Done")
            const lost = all.length - entries.length;
            const tally = `${entries.length} of ${all.length}${lost ? ` — ${lost} failed` : ''}`;
            // J16-11: "none of the 1 files" -> the file's own name
            if (!entries.length) { this.setState({ outcome: all.length === 1 ? `${all[0].file.name} could not be made.` : `None of the ${all.length} files could be made.` }); this.props.onTelemetry?.('EDITOR_EXPORT_PARTIAL', { mode, ok: 0, failed: failed.length }); return; }   // the failure box says why
            if (mode === 'each') {
                // BU-5: all N arrive, in order (a failed file never holds the rest back)
                const n = await saveSequentially(entries, saveAs, { cancelled, onProgress: d => { this.savedInRun = d; } });
                if (!cancelled()) this.setState({ outcome: lost ? `Saved ${tally}.` : eachDoneText(n) });
                if (failed.length && !partial) this.props.onTelemetry?.('EDITOR_EXPORT_PARTIAL', { mode, ok: entries.length, failed: failed.length });
                // J11-3 / J15-2: 'each' is a finished export too
                if (!cancelled()) this.props.onTelemetry?.('EDITOR_EXPORT_DONE', { mode, files: n, bulk: true });
                return;
            }
            if (failed.length && !partial) { this.setState({ outcome: `Done: ${tally.replace(' — ', ' ready — ')}. Nothing is saved yet.` }); this.props.onTelemetry?.('EDITOR_EXPORT_PARTIAL', { mode, ok: entries.length, failed: failed.length }); return; }
            let saved: string[] = [];
            if ((mode === 'single') && entries[0]) { saveAs(entries[0].blob, entries[0].name); saved = [entries[0].name]; }
            if (mode === 'share' && entries[0]) { await this.shareFile(new File([entries[0].blob], entries[0].name, { type: entries[0].blob.type })); saved = [entries[0].name]; }
            let doneMode: string = mode;
            if (mode === 'shareAll') { const r = await this.shareFiles(entries, saveAs); saved = r.names; if (!r.shared) doneMode = 'each'; }
            if (mode === 'zip' && entries.length) {
                saved = await this.saveZips(entries, saveAs, total);
                // formats-11: the ZIP without the failed files is saved - the failure box steps aside
                if (partial || lost) this.setState({ failureAck: this.failedKey() });
            }
            // the visible outcome line, only now that the save was handed to the browser (J3-2, J3-4)
            this.setState({ outcome: lost ? `${this.doneText(doneMode, saved, entries.length, all.length, [])} Saved ${tally}.` : this.doneText(doneMode, saved, entries.length, all.length, []) });
            this.props.onTelemetry?.('EDITOR_EXPORT_DONE', { mode, files: entries.length, bulk: true });
        } catch (e: any) {
            this.setState({ fatal: offline() ? OFFLINE : (e?.message || 'The files could not be saved.') });
            this.props.onTelemetry?.('EDITOR_EXPORT_FAILED', { reason: String(e?.message || e).slice(0, 80), bulk: true });
        } finally {
            if (token === this.runToken && this.live) this.setState({ exporting: null, preparing: null });
        }
    };
    /** Identity of an export's output: the file, the edit it gets, and the output settings. */
    private resultKey(it: EditorItem, master: EditorItem | undefined): string {
        const { format, quality } = this.state;
        return JSON.stringify([it.file.name, it.file.size, it.file.lastModified, it.route, this.stateFor(it, master), format, quality]);
    }
    /** C1 "Try again": counts a retry on every failed file, then runs the batch again. */
    private retryFailed = () => {
        this.setState(s => ({ items: s.items.map(it => it.status === 'failed' && !it.error ? { ...it, retries: (it.retries ?? 0) + 1 } : it) }), () => this.exportBulk('zip'));
    };
    private cancelBulk = () => {
        this.runToken++;
        this.exportLock = false;   // IMAGEEDITOR-21: a cancelled run does not hold the next Download back
        this.state.items.forEach(it => it.job?.cancel());
        // J3-4: Cancel is said on screen, and never followed by "Done"
        const n = this.savedInRun;
        this.setState(s => ({ exporting: null, preparing: null, outcome: n ? `Cancelled after ${n} ${n === 1 ? 'file' : 'files'} were saved.` : 'Cancelled. Nothing was saved.',
            items: s.items.map(it => it.status === 'preparing' || it.status === 'queued' ? { ...it, status: 'queued', job: null, progress: null } : it) }));
        this.props.onTelemetry?.('EDITOR_EXPORT_CANCELLED', {});
    };
    private firstFailed(): EditorItem | undefined { return this.state.items.find(it => it.error || it.status === 'failed'); }
    private failedItems(): EditorItem[] { return this.state.items.filter(it => it.error || it.status === 'failed'); }
    private unreadableNames(): string[] { return this.state.items.filter(it => it.error).map(it => it.file.name); }
    private failedKey(): string { return this.failedItems().map(it => it.id).join('|'); }
    private failMessage(it: EditorItem): string {
        const raw = it.failure?.message || it.error || 'could not be processed.';
        // critic-5: the failure's code picks the sentence (5xx = our server, 429 = the limit + its wait)
        const code = it.failure?.code;
        if (code === 'server_error' || code === 'rate_limited') return failureText(it.file.name, raw.replace(`${it.file.name}: `, ''), code, it.failure?.retryAfterSec);
        return raw.includes(it.file.name) ? raw : failureText(it.file.name, raw);
    }
    /** Preparing line (visual-7): the count is on the button ("Preparing 7/12"); this line adds the time
     *  left only - one count, never two. Files in flight feed the ETA (PLAN C3). */
    private prepLabel(p: { done: number; total: number; startedAt: number }): string {
        const active = this.state.items.filter(it => it.status === 'preparing' && it.startedAt).map(it => ({ startedAt: it.startedAt as number, fraction: it.progress?.fraction ?? 0, phase: it.progress?.phase }));
        const eta = (etaLabel as (d: number, t: number, ms: number, a?: unknown) => string)(p.done, p.total, Date.now() - p.startedAt, active)
            .replace(/^\s*\d+\s+of\s+\d+\s*(·\s*)?/, '').trim();
        // IMAGEEDITOR-13: until a time left is known, the stage our servers report ("Converting the EPS…"), not "Starting…"
        const staged = this.state.items.find(it => it.status === 'preparing' && it.progress?.label)?.progress?.label;
        if (!eta && staged) return staged;
        return prepText(p.done, p.total, eta);
    }
    private removedTimer: ReturnType<typeof setTimeout> | null = null;
    private removeItem = (id: string) => {
        this.setState(s => {
            const idx = s.items.findIndex(it => it.id === id); if (idx < 0) return null;
            const gone = s.items[idx];
            gone.job?.cancel();   // its upload / edge job stops with it
            const items = s.items.filter(it => it.id !== id);
            const selected = Math.max(0, Math.min(items.length - 1, s.selected > idx ? s.selected - 1 : s.selected));
            // IMAGEEDITOR-18b: kept for Undo, as it was (own crop, decoded pixels); a run it was in is over for it
            const kept: EditorItem = { ...gone, job: null, progress: null, status: gone.status === 'preparing' ? 'queued' : gone.status };
            // J16-11: the last run's "Saved 4 of 5 — 1 failed" is stale once the batch changed
            return { items, selected, preview: s.selected === idx ? null : s.preview, outcome: '', removed: items.length ? { item: kept, index: idx } : null } as any;
        }, () => {
            if (!this.state.items.length) { this.props.onChangeFiles(); return; }
            this.props.onFilesChange?.(this.state.items.map(it => it.file));
            this.schedulePreview(true);
            if (this.removedTimer) clearTimeout(this.removedTimer);
            this.removedTimer = setTimeout(() => { this.removedTimer = null; if (this.live) this.setState({ removed: null }); }, REMOVED_UNDO_MS);
        });
        this.props.onTelemetry?.('EDITOR_FILE_REMOVED', {});
    };
    /** IMAGEEDITOR-18b: Undo of a removal - the file is back at its place, with its own crop, and selected. */
    private undoRemove = () => {
        const r = this.state.removed; if (!r) return;
        if (this.removedTimer) { clearTimeout(this.removedTimer); this.removedTimer = null; }
        if (this.state.items.some(x => x.file === r.item.file) || this.state.items.length >= BATCH_LIMIT) { this.setState({ removed: null }); return; }
        const at = Math.min(r.index, this.state.items.length);
        this.setState(s => {
            const items = [...s.items]; items.splice(at, 0, r.item);
            return { items: this.withViews(items), selected: at, preview: null, removed: null, outcome: '' };
        }, () => {
            this.props.onFilesChange?.(this.state.items.map(it => it.file));
            this.props.onTelemetry?.('EDITOR_FILE_RESTORED', {});
        });
    };
    private askReplace = (id: string) => { this.replaceId = id; this.replaceRef.current?.click(); };
    private onReplaceChosen = async (e: { target: HTMLInputElement }) => {
        const file = e.target.files?.[0]; const id = this.replaceId; this.replaceId = null;
        if (!file || !id) return;
        const fresh = newItem(file, Date.now());
        if (this.props.bulk && this.server) fresh.route = this.server.routeFor(detectFormat(file), true);
        let idx = -1;
        await new Promise<void>(r => this.setState(s => { idx = s.items.findIndex(it => it.id === id); return { items: s.items.map(it => it.id === id ? fresh : it), outcome: '', fatal: null }; }, r));
        this.props.onFilesChange?.(this.state.items.map(it => it.file));
        if (idx >= 0) {
            if (this.props.bulk && !this.browserCanShow(fresh)) await this.probeItem(fresh.id); else await this.ensureDecoded(idx);
            if (idx === this.state.selected) this.schedulePreview(true);
        }
    };
    private bulkInspector(): BulkInspector | undefined {
        if (!this.props.bulk) return undefined;
        const { items, preparing } = this.state;
        // power-8: a file that failed on its route stays counted on that route (only a file the browser
        // skipped outright - it.error, never sent anywhere - is left out)
        // fidelity N1: an untouched server-format file goes into the download as it is, from the browser -
        // it is not counted as uploaded until an edit (or a format / quality change) really sends it
        const routeOf = (it: EditorItem) => it.route === 'server' && !it.sent && this.memberUntouched(it) ? 'browser' : it.route;
        const counts = batchCounts(items.map(it => ({ name: it.file.name, route: routeOf(it), failed: !!it.error && it.route !== 'server', format: formatLabel(it.decoded?.sourceFormat ?? detectFormat(it.file)) })));
        const bad = this.firstFailed();
        const failedAll = this.failedItems();
        const acked = !!bad && this.state.failureAck === this.failedKey();
        const msg = !bad ? '' : failedAll.length > 1 ? `${failedAll.length} files couldn't be used: ${failedAll.map(it => it.file.name).join(', ')}.` : this.failMessage(bad);
        // privacy line data (C4): files whose bytes reached our servers this session, failed ones included
        const uploaded = items.filter(it => it.sent).length;
        return {
            routeCounts: { browser: counts.browser, server: counts.server, serverFormats: counts.serverFormats, uploaded } as BulkInspector['routeCounts'],
            ownCrop: !!this.current?.ownCrop, onOwnCrop: this.onOwnCrop,
            preparing: preparing ? { label: this.prepLabel(preparing), onCancel: this.cancelBulk } : null,
            // formats-11: once the ZIP without the failed files is saved, the box says so and offers only Replace / Remove
            failure: bad ? { message: acked ? `Saved the ZIP without ${failedAll.length === 1 ? 'this file' : `these ${failedAll.length} files`}. ${msg}` : msg,
                okCount: acked ? 0 : items.filter(it => !it.error && it.status !== 'failed').length,
                onDownload: () => this.exportBulk('zip', true), onReplace: () => this.askReplace(bad.id), onRemove: () => this.removeItem(bad.id),
                // NEW-3 (last mile 2): with 2+ failed files every listed file gets its own Replace / Remove
                onReplaceFile: (id: string) => this.askReplace(id), onRemoveFile: (id: string) => this.removeItem(id),
                // critic-5: Replace only when the file itself is the problem; Try again only for a transient fault
                canReplace: failedAll.some(it => failureOffersReplace(it.failure?.code ?? (it.error ? 'decode' : undefined))),
                // C1 (J2-1): Try again only for a lost connection or a timeout, and at most MAX_RETRIES times per file
                onRetry: failedAll.some(it => canRetry(it.failure?.code, it.retries ?? 0)) ? this.retryFailed : undefined,
                files: failedAll.map(it => ({ id: it.id, name: it.file.name, message: this.failMessage(it).replace(`${it.file.name}: `, '') })) } as BulkInspector['failure'] : null,
        };
    }

    // ------------------------------------------------------------ render
    render() {
        const { items, selected, tool, view, zoom, preview, sourcePreview, basePreview, busy, fatal } = this.state;
        const it = items[selected];
        const st = it?.state ?? initialState({ width: 1, height: 1 });
        const out = savedSize(st, it ? this.outFormatFor(it) : 'jpg');   // C2: the caption states the file's real size
        const noneReadable = items.length > 1 && items.every(x => x.error);
        // IMAGEEDITOR-28 / -29-30b: with the flag off the error banner above says it (with Replace / Remove) - the stage
        // does not repeat it; one file that could not be read gets no tool tabs either
        const unreadableOne = items.length === 1 && !!it?.error && !it.decoded;
        const outputNote = noneReadable ? (this.props.bulk ? noneReadableText(items.length) : NOTHING_TO_PREVIEW)
            // IMAGEEDITOR-28 (review 10 loop): flag on too - the failure box says the error, the stage does not repeat it
            : unreadableOne ? NOTHING_TO_PREVIEW
            : it?.error ? it.error
            : this.props.bulk && it && !this.browserCanShow(it) ? `${it.file.name} is edited on our servers: no preview here. ${items.length > 1 ? 'It gets the batch settings.' : 'Your crop, rotation, flip and size are applied there.'}`
            : items.length > 1 ? (this.props.bulk ? 'Preview shows the selected photo. Every file gets the same settings; click a thumbnail to give one its own crop.' : 'Preview shows the selected photo. Every file gets the same settings.')
            : view === 'actual' ? `Shown at actual size: ${out.width} × ${out.height} px.`
            : view === 'original' ? `Showing the untouched original (${st.source.width} × ${st.source.height} px).`
            // regression-9: "scaled to fit" only when the preview really is scaled
            : Math.abs(zoom - 1) < 0.005 ? `Shown at full size. The file you download will be ${out.width} × ${out.height} px.`
            : `Preview is scaled to fit. The file you download will be ${out.width} × ${out.height} px.`;
        const bulk = this.props.bulk;
        const thumbs: StageThumb[] = items.map(x => {
            // visual-12: a file the browser never opens shows its header size, or the size our servers returned
            const known = x.decoded ? { width: x.decoded.width, height: x.decoded.height } : x.probed ?? (x.status === 'done' && x.resultSize ? x.resultSize : null);
            // owner 2026-10-10: the card is the file's effective edit (its `strip`, else the source thumb until drawn) and says
            // its output size - from its view state, the export's state but for markers (model/thumbs thumbOut)
            const th: StageThumb = { id: x.id, name: x.file.name, size: known ?? { width: 0, height: 0 }, thumb: x.strip?.canvas ?? x.thumb, error: x.error ?? undefined,
                out: thumbOut(x.state, this.member(x).size, this.outFormatFor(x)), edited: !!x.state && isEdited(x.state) };
            if (bulk) {
                // SP-6: a tick only while the file's result still matches the current settings
                th.bulk = { route: x.route, status: x.status === 'done' && !this.freshResult(x) ? 'queued' : x.status, ownCrop: !!x.ownCrop, format: formatLabel(x.decoded?.sourceFormat ?? detectFormat(x.file)), dim: !!this.state.preparing && x.status === 'queued' };
                if (x.failure) th.error = this.failMessage(x);
            }
            return th;
        });
        const ownCount = bulk ? items.filter(x => x.ownCrop).length : 0;
        const ownNote = ownCount ? ` · ${ownCount} ${ownCount === 1 ? 'has its' : 'have their'} own crop` : '';
        const fileInfo = it?.decoded ? `${it.decoded.width} × ${it.decoded.height} · ${formatBytes(it.file.size)}` : it?.error ? 'could not open'
            : bulk && it && it.route === 'server' && it.probed !== undefined ? `${it.probed ? `${it.probed.width} × ${it.probed.height}` : 'size read on our servers'} · ${formatBytes(it.file.size)}`
            : 'opening…';
        return <div className={styles.root}>
            {this.state.codecsFailed && <div className={styles.errorNote} role="alert" data-testid="codecs-failed">{CODECS_FAILED}{' '}
                <button type="button" className={styles.link} data-testid="codecs-retry" onClick={() => { void this.retryCodecs(); }}>Try again</button></div>}
            {fatal && <div className={styles.errorNote} role="alert">{fatal} <button type="button" className={styles.link} onClick={() => this.setState({ fatal: null })}>Dismiss</button></div>}
            {/* power-3 / critic-6: files that were not added, said where the user is looking */}
            {this.props.notice && <div className={`${styles.errorNote} ${styles.undoNote}`} style={TOAST} role="alert" data-testid="editor-notice">{this.props.notice}{' '}
                <button type="button" className={styles.link} onClick={() => this.props.onDismissNotice?.()}>Dismiss</button></div>}
            {/* phone-5: Reset / Reset all are one undo step - say so, with the Undo right there */}
            {this.state.resetNote && <div className={styles.undoNote} style={TOAST} role="status" data-testid="reset-note">{this.state.resetNote}{' '}
                <button type="button" className={styles.link} data-testid="reset-undo" onClick={this.undo}>Undo</button>
                <button type="button" className={styles.link} aria-label="Dismiss" onClick={() => this.setState({ resetNote: null })}>×</button></div>}
            {/* IMAGEEDITOR-18b: a removed thumbnail can be brought back for a few seconds (polite: role=status) */}
            <div className={this.state.removed ? styles.undoNote : styles.outcome} style={this.state.removed ? TOAST : undefined} data-testid="removed-note">
                <span role="status" data-testid="removed-live">{this.state.removed ? `Removed ${this.state.removed.item.file.name}` : ''}</span>
                {this.state.removed && <>{' · '}<button type="button" className={styles.link} data-testid="removed-undo" onClick={this.undoRemove}>Undo</button>
                    <button type="button" className={styles.link} aria-label="Dismiss" onClick={() => this.setState({ removed: null })}>×</button></>}</div>
            {/* E1 .outcome (J6-6, J8-10, J11-11, J11-4): how the last export ended, ON SCREEN - written only after the
                save was handed to the browser, or after Cancel / a failure in its own words (J3-4) */}
            <div className={styles.outcome} style={this.state.outcome ? TOAST : undefined} data-testid="outcome-line">
                <span role="status" data-testid="export-outcome">{this.state.outcome}</span>
                {this.state.outcome && <>{' '}<button type="button" className={styles.link} aria-label="Dismiss" onClick={() => this.setState({ outcome: '' })}>×</button></>}</div>
            {/* J6-4 / J13-2 (flag off): a file the browser could not open offers Replace / Remove right here - never a
                dead end where the only live green button is an ad */}
            {!this.props.bulk && it?.error && <div className={styles.errorNote} role="alert" data-testid="unreadable-actions">{noneReadable ? noneReadableText(items.length) : <>{items.length > 1 ? `${it.file.name}: ` : ''}{it.error}</>}{' '}
                <button type="button" className={styles.link} data-testid="unreadable-replace" onClick={() => this.askReplace(it.id)}>Replace this file</button>{' '}
                <button type="button" className={styles.link} data-testid="unreadable-remove" onClick={() => this.removeItem(it.id)}>Remove it</button></div>}
            <div className={styles.editor} ref={this.editorRef}>
                {!unreadableOne && <ToolTabs state={st} tool={tool} onTool={this.onTool} />}
                <StageComponent
                    // regression-3: a photo the browser could not open has nothing to resize or crop - no grips
                    // drawn on its 1 x 1 stand-in (QA read the four overlapping grips as a spinner)
                    state={st} tool={it?.error && !it.decoded ? 'flip' : tool} view={view} zoom={zoom}
                    preview={preview} sourcePreview={sourcePreview} basePreview={basePreview}
                    onFullscreen={this.state.fsSupported ? this.toggleFullscreen : undefined} isFullscreen={this.state.fullscreen}
                    history={{ canUndo: this.state.past.length > 0, canRedo: this.state.future.length > 0, edited: TOOL_LIST.some(t => isApplied(st, t)) || !!it?.ownCrop }}
                    onUndo={this.undo} onRedo={this.redo} onResetAll={this.resetAll} onResetBatch={it?.ownCrop ? this.resetBatch : undefined} onDragging={this.onDragging} boxHeight={this.state.boxHeight} showGrid={this.state.angleDragging}
                    onZoom={(z, v) => this.setState({ zoom: z, view: v })}
                    // IMAGEEDITOR-29-30b (loop): junk files are not "2 photos · 28 B · settings apply to all"
                    fileName={items.length > 1 ? `${items.length} ${noneReadable ? 'files' : 'photos'}` : (it?.file.name ?? '')}
                    fileInfo={items.length > 1 ? (noneReadable ? 'none could be read' : `${formatBytes(items.reduce((a, x) => a + x.file.size, 0))} · settings apply to all${ownNote}`) : fileInfo}
                    // IMAGEEDITOR-28 (loop): nothing to show - Zoom, Fit, 100%, Original and Full screen are disabled
                    empty={!!it?.error && !it.decoded}
                    // firsttime-12: one photo open - "Add more" adds a second one (the page promises up to 50);
                    // it never discards the photo being edited. A wrong file is removed with its × once there are two.
                    changeLabel="Add more"
                    onChangeFile={this.props.onAddMore}
                    busy={busy} onState={this.setEditState} outputNote={outputNote}
                    // E1: a photo the browser does not open shows a plain line, never a 1 x 1 photo or an endless spinner (J2-6, J14-4, J16-5)
                    placeholder={bulk && it && !it.decoded && !it.error && !this.browserCanShow(it) ? `No preview here: ${it.file.name} is edited on our servers.` : undefined}
                    batch={items.length > 1 ? { items: thumbs, selected, onSelect: i => this.setState({ selected: i, preview: null }), onAddMore: this.props.onAddMore, remaining: BATCH_LIMIT - items.length,
                        onRemove: this.removeAt, onClear: this.clearBatch } : null}
                />
                <InspectorComponent
                    state={st} tool={tool} onTool={this.onTool}
                    onState={this.setEditState} onResetAll={this.resetAll} onResetTool={this.resetToolFit}
                    sourceFormat={it?.decoded?.sourceFormat ?? (bulk && it ? detectFormat(it.file) : 'unknown')} originalBytes={it?.file.size ?? 0}
                    format={this.state.format} onFormat={this.onFormat}
                    quality={this.state.quality} onQuality={q => this.setState({ quality: q, failureAck: null })}
                    output={this.outputInfo()} batchCount={items.length}
                    cropPolicy={this.state.cropPolicy} onCropPolicy={this.onCropPolicy}
                    onAngleDragging={d => { if (d !== this.state.angleDragging) this.setState({ angleDragging: d }); }}
                    exporting={this.state.exporting} onExport={this.onExport} canShare={this.state.canShare}
                    bulk={this.bulkInspector()}
                    uploadedNow={bulk ? !!it?.sent : undefined}
                    // D1: W/H are disabled while the photo is still being opened (decoding, or its header being read);
                    // a RAW whose size no header gives is not "being read" - its output says so itself (sourceUnknown)
                    sizeKnown={!(it && !it.error && !it.decoded && (bulk ? (this.browserCanShow(it) || it.probed === undefined) : true))}
                    codecsFailed={!!this.state.codecsFailed}
                />
                {(bulk || items.some(x => x.error)) && <input type="file" hidden ref={this.replaceRef} data-testid="replace-input" onChange={this.onReplaceChosen} />}
                {/* Reserved ad band across the bottom of the editor: the stage + inspector take
                    (100dvh − band) so the canvas fits ABOVE it on any aspect (owner 2026-09-08).
                    The floating anchor is suppressed while the editor is open (data-xc-editor set in
                    componentDidMount) so this is the only bottom ad. */}
                <div className={styles.editorAdBand}>
                    <MotiveComponent
                        data={AdSlots.aboveFooter} containerWidth="100%" containerHeight="90px"
                        adWidth="970px" adHeight="90px" showAds={true}
                        location={MotiveLocationTypes.convertPageBottom} className="width_major"
                    ></MotiveComponent>
                </div>
            </div>
        </div>;
    }
}

export { LOSSY, orientedSize };
